<template>
  <v-container fluid class="ncrm-lead-board">
    <v-card class="pa-6">
      <div class="d-flex flex-wrap align-center justify-space-between ga-4">
        <div class="text-left">
          <h2 class="text-h5 ncrm-text-strong">
            {{ board?.name || $t("ncrm.leads.board.titleFallback") }}
          </h2>
          <div v-if="board" class="d-flex flex-wrap align-center ga-2 mt-2">
            <v-chip color="primary">
              {{ entityScopeLabel(board.entity_scope) }}
            </v-chip>
            <v-chip variant="outlined">
              {{ $t("ncrm.leads.board.openEntries", { count: totalOpenEntries }) }}
            </v-chip>
          </div>
        </div>
        <div class="d-flex flex-wrap align-center ga-2">
          <v-btn variant="text" color="primary" @click="openBoardSettings">
            <v-icon start>mdi-cog-outline</v-icon>
            {{ $t("ncrm.leads.actions.boardSettings") }}
          </v-btn>
          <v-btn variant="text" color="primary" @click="openCardLayoutDialog">
            <v-icon start>mdi-view-grid-plus-outline</v-icon>
            {{ $t("ncrm.leads.actions.cardSettings") }}
          </v-btn>
          <v-btn variant="text" color="primary" :loading="entriesLoading" @click="loadEntries">
            <v-icon start>mdi-refresh</v-icon>
            {{ $t("ncrm.leads.actions.refresh") }}
          </v-btn>
          <v-btn color="primary" @click="openAddDialog">
            <v-icon start>mdi-plus</v-icon>
            {{ $t("ncrm.leads.actions.addLeads") }}
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-card class="mt-4">
      <v-card-text>
        <NcrmLeadBoardToolbar
          v-model:view="activeView"
          v-model:search="search"
          :view-items="viewItems"
          :preferred-view="preferredView"
          :filters="advancedFilters"
          :density-cycle-disabled="densityCycleDisabled"
          :density-cycle-aria-label="densityCycleAriaLabel"
          :density-cycle-tooltip="densityCycleTooltip"
          :density-state-icon="densityStateIcon"
          @set-default="setPreferredView"
          @open-filters="advancedFiltersDialog = true"
          @remove-filter="removeAdvancedFilter"
          @cycle-density="cycleDensity"
          @reset-filters="resetFilters"
        />

        <div
          v-if="activeView === 'board'"
          class="ncrm-lead-board__columns"
          :class="`ncrm-lead-board__columns--${effectiveDensity}`"
        >
          <section
            v-for="(column, columnIndex) in boardColumns"
            :key="`lead-stage-${column.stage_id}`"
            class="ncrm-lead-board__column"
            :class="[
              `ncrm-lead-board__column--${effectiveDensity}`,
              columnDragClasses(column),
            ]"
          >
            <div
              class="ncrm-lead-board__column-accent"
              :style="{ background: columnAccentStyle(columnIndex) }"
            />
            <header class="ncrm-lead-board__column-header">
              <span class="ncrm-text-strong">{{ column.stage_name }}</span>
              <v-chip size="x-small" :color="columnAccentColor(columnIndex)">
                {{ stageCountLabel(column) }}
              </v-chip>
            </header>
            <div
              class="ncrm-lead-board__column-body"
              @dragover.prevent="onStageDragOver($event, column)"
              @dragenter.prevent="onStageDragOver($event, column)"
              @drop.prevent="onStageDrop(column)"
            >
              <div
                v-if="!column.items.length"
                class="ncrm-lead-board__column-empty"
              >
                <v-icon size="22" class="ncrm-lead-board__column-empty-icon">mdi-inbox-outline</v-icon>
                <span>{{ $t("ncrm.leads.board.emptyStage") }}</span>
              </div>

              <div v-else class="ncrm-lead-board__column-items">
                <v-card
                  v-for="entry in column.visible_items"
                  :key="`entry-${entry.id}`"
                  class="ncrm-lead-board__entry"
                  :class="[
                    `ncrm-lead-board__entry--${effectiveDensity}`,
                    { 'ncrm-lead-board__entry--dragging': draggingEntryId === Number(entry.id) },
                    { 'ncrm-lead-board__entry--overdue': isNextActivityOverdue(entry.next_followup_at) },
                  ]"
                  variant="outlined"
                  role="button"
                  :tabindex="resolveLeadRoute(entry) ? 0 : -1"
                  :draggable="isEntryDraggable(entry)"
                  @click="handleEntryCardActivate(entry)"
                  @keydown.enter.prevent="handleEntryCardActivate(entry)"
                  @keydown.space.prevent="handleEntryCardActivate(entry)"
                  @dragstart="onEntryDragStart($event, entry, column)"
                  @dragend="onEntryDragEnd"
                >
                  <v-card-text :class="entryCardPaddingClass">
                    <div class="d-flex align-start justify-space-between ga-2">
                      <div class="text-body-2 ncrm-text-strong ncrm-lead-board__entry-title" :title="entryDisplayName(entry)">
                        {{ entryDisplayName(entry) }}
                      </div>
                      <div class="d-flex align-center ga-1 flex-shrink-0">
                        <v-chip
                          v-if="shouldShowEntryPriority(entry.priority)"
                          size="x-small"
                          :color="entryPriorityColor(entry.priority)"
                          class="ncrm-lead-board__entry-priority-chip"
                        >
                          {{ entryPriorityLabel(entry.priority) }}
                        </v-chip>
                        <v-chip
                          size="x-small"
                          :color="entryStatusColor(entry.status)"
                          class="ncrm-lead-board__entry-status-chip"
                        >
                          {{ entryStatusLabel(entry.status) }}
                        </v-chip>
                      </div>
                    </div>

                    <div class="ncrm-lead-board__entry-fields mt-1">
                      <div
                        v-for="field in resolvedEntryCardFields(entry)"
                        :key="`entry-${entry.id}-field-${field.key}`"
                        class="ncrm-lead-board__entry-field"
                      >
                        <span
                          class="text-caption text-medium-emphasis ncrm-lead-board__entry-field-label"
                          :title="field.label"
                        >
                          {{ field.label }}:
                        </span>
                        <div v-if="field.variant === 'chip'" class="ncrm-lead-board__entry-field-value d-flex align-center">
                          <v-chip
                            v-if="field.value"
                            size="x-small"
                            :color="field.color || 'primary'"
                            class="ncrm-lead-board__entry-field-chip"
                          >
                            {{ field.value }}
                          </v-chip>
                          <span v-else class="text-caption text-medium-emphasis">-</span>
                        </div>
                        <span
                          v-else
                          :class="[
                            'text-body-2',
                            'ncrm-lead-board__entry-field-value',
                            { 'ncrm-lead-board__entry-field-value--overdue': field.overdue },
                          ]"
                          :title="field.value || '-'"
                        >
                          {{ field.value || "-" }}
                        </span>
                      </div>
                    </div>

                    <div class="ncrm-lead-board__entry-footer mt-2">
                      <div class="ncrm-lead-board__entry-footer-timestamp">
                        <v-icon size="11" class="ncrm-lead-board__entry-footer-icon">mdi-clock-outline</v-icon>
                        {{ formatDateTime(entry.updated_at) }}
                      </div>
                      <div class="d-flex align-center ga-1">
                        <v-btn
                          icon="mdi-chevron-left"
                          size="x-small"
                          variant="text"
                          color="primary"
                          :disabled="entryMoveLoadingId === entry.id || !canMoveEntry(entry, -1)"
                          @click.stop="moveEntryByOffset(entry, -1)"
                        />
                        <v-btn
                          icon="mdi-chevron-right"
                          size="x-small"
                          variant="text"
                          color="primary"
                          :disabled="entryMoveLoadingId === entry.id || !canMoveEntry(entry, 1)"
                          @click.stop="moveEntryByOffset(entry, 1)"
                        />
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <v-btn
                  v-if="column.has_more"
                  size="x-small"
                  variant="text"
                  color="primary"
                  class="ncrm-lead-board__load-more"
                  :disabled="entriesLoading"
                  @click="loadMoreStageEntries(column.stage_id)"
                >
                  {{ stageLoadMoreLabel(column) }}
                </v-btn>
              </div>
            </div>
            <div
              v-if="columnIndex < boardColumns.length - 1"
              class="ncrm-lead-board__column-divider"
            />
          </section>
        </div>

        <v-data-table-server
          v-else
          :headers="tableHeaders"
          :items="entries"
          :items-length="totalItems"
          item-key="id"
          class="elevation-0 ncrm-lead-board__table"
          density="compact"
          :loading="entriesLoading"
          :row-props="tableRowProps"
          v-model:sort-by="sortBy"
          v-model:page="page"
          v-model:items-per-page="itemsPerPage"
          @click:row="handleEntryRowClick"
        >
          <template #item.entity="{ item }">
            <div class="d-flex align-center ga-2">
              <v-icon size="14" class="ncrm-lead-board__table-cell-icon">
                {{ item.entity_type === 'person' ? 'mdi-account-outline' : 'mdi-domain' }}
              </v-icon>
              <div class="text-body-2 ncrm-text-strong ncrm-lead-board__table-cell-inline" :title="compactEntityCellLabel(item)">
                {{ compactEntityCellLabel(item) }}
              </div>
            </div>
          </template>

          <template #item.last_call="{ item }">
            <NcrmLastCallCell :last-call-at="item.last_call_at || ''" :has-pbx="Boolean(item.last_call_has_pbx)" />
          </template>
          <template #item.stage_name="{ item }">
            <v-chip size="x-small" variant="outlined">
              {{ item.stage_name || "-" }}
            </v-chip>
          </template>

          <template #item.status="{ item }">
            <v-chip size="x-small" :color="entryStatusColor(item.status)">
              {{ entryStatusLabel(item.status) }}
            </v-chip>
          </template>

          <template #item.owner_name="{ item }">
            <span class="ncrm-lead-board__table-cell-inline" :title="ownerLabel(item)">{{ ownerLabel(item) }}</span>
          </template>

          <template #item.updated_at="{ item }">
            <div class="d-flex align-center ga-1 text-caption text-medium-emphasis" style="white-space: nowrap">
              <v-icon size="12" style="opacity: 0.55">mdi-clock-outline</v-icon>
              {{ formatDateTime(item.updated_at) }}
            </div>
          </template>

          <template #item.row_nav>
            <v-icon size="16" class="ncrm-lead-board__table-chevron">mdi-chevron-right</v-icon>
          </template>

          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.leads.board.empty") }}
            </v-alert>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <NcrmLeadBoardFiltersDrawer
      v-model="advancedFiltersDialog"
      :board-id="boardId"
      :filters="advancedFilters"
      @apply="applyAdvancedFilters"
    />

    <NcrmRecordEditDialog
      v-model="addDialog"
      :title="$t('ncrm.leads.addDialog.title')"
      :subtitle="$t('ncrm.leads.addDialog.subtitle')"
      :loading="addSaving"
      :save-disabled="addSaveDisabled"
      :max-width="980"
      :extra-action-label="$t('ncrm.leads.addDialog.validate')"
      :extra-action-disabled="addValidating || addSaving"
      @extra-action="validateBulkAdd"
      @save="submitBulkAdd"
    >
      <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-3">
        <div class="text-subtitle-2 ncrm-text-strong">{{ $t("ncrm.leads.addDialog.rowsTitle") }}</div>
        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          prepend-icon="mdi-plus"
          :disabled="addSaving"
          @click="addBulkRow"
        >
          {{ $t("ncrm.leads.addDialog.addRow") }}
        </v-btn>
      </div>

      <div class="d-flex flex-column ga-3">
        <v-card
          v-for="(row, index) in addRows"
          :key="row._uid"
          variant="outlined"
          class="pa-3"
        >
          <div class="d-flex align-center justify-space-between ga-2 mb-2">
            <div class="text-caption text-medium-emphasis">
              {{ $t("ncrm.leads.addDialog.rowIndex", { index: index + 1 }) }}
            </div>
            <v-btn
              icon="mdi-close"
              size="x-small"
              variant="text"
              color="error"
              :disabled="addRows.length <= 1 || addSaving"
              @click="removeBulkRow(index)"
            />
          </div>

          <v-row>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="row.owner_user_id"
                :label="$t('ncrm.leads.addDialog.fields.owner')"
                :items="ownerOptions"
                item-title="label"
                item-value="id"
                :loading="ownerOptionsLoading"
                :menu-props="selectMenuProps"
                clearable
                no-filter
                @update:search="handleOwnerSearch"
                @update:model-value="markBulkRowsDirty"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="row.entity_type"
                :items="entityTypeOptions"
                item-title="label"
                item-value="value"
                :label="$t('ncrm.leads.addDialog.fields.entityType')"
                :menu-props="selectMenuProps"
                :disabled="isEntityTypeLocked"
                @update:model-value="onBulkRowEntityTypeChanged(row)"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="row.stage_id"
                :items="stageOptions"
                item-title="label"
                item-value="id"
                :label="$t('ncrm.leads.addDialog.fields.stage')"
                :menu-props="selectMenuProps"
                clearable
                @update:model-value="markBulkRowsDirty"
              />
            </v-col>

            <v-col cols="12" md="6">
              <NcrmDateTimeField
                v-model="row.next_followup_at"
                :label="$t('ncrm.leads.addDialog.fields.nextFollowup')"
                @update:model-value="markBulkRowsDirty"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="row.conditions_logic"
                :items="conditionLogicOptions"
                item-title="label"
                item-value="value"
                :label="$t('ncrm.leads.addDialog.fields.conditionLogic')"
                :menu-props="selectMenuProps"
                @update:model-value="markBulkRowsDirty"
              />
            </v-col>
          </v-row>

          <v-divider class="my-2" />

          <div class="d-flex align-center justify-space-between ga-2 mb-2">
            <div class="text-caption text-medium-emphasis">
              {{ $t("ncrm.leads.addDialog.conditionsTitle") }}
            </div>
            <v-btn
              size="x-small"
              variant="tonal"
              color="primary"
              prepend-icon="mdi-plus"
              :disabled="addSaving"
              @click="addConditionRow(row)"
            >
              {{ $t("ncrm.leads.addDialog.addCondition") }}
            </v-btn>
          </div>

          <v-row
            v-for="(condition, conditionIndex) in row.conditions"
            :key="condition._uid"
            class="ncrm-lead-board__condition-row"
          >
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="condition.field_key"
                :label="$t('ncrm.leads.addDialog.fields.filterField')"
                :items="filterFieldOptionsForEntity(row.entity_type)"
                item-title="label"
                item-value="key"
                :menu-props="selectMenuProps"
                clearable
                @update:model-value="onConditionFieldChanged(row, condition)"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-select
                v-model="condition.operator"
                :label="$t('ncrm.leads.addDialog.fields.operator')"
                :items="conditionOperatorOptions"
                item-title="label"
                item-value="value"
                :menu-props="selectMenuProps"
                @update:model-value="onConditionOperatorChanged(row, condition)"
              />
            </v-col>

            <v-col
              v-if="condition.operator !== 'is_empty' && condition.operator !== 'is_not_empty'"
              cols="12"
              md="4"
            >
              <v-combobox
                v-model="condition.value"
                :label="$t('ncrm.leads.addDialog.fields.value')"
                :items="condition.value_suggestions"
                :loading="condition.value_loading"
                clearable
                :menu-props="selectMenuProps"
                @update:search="(search) => scheduleConditionValueSearch(row, condition, search)"
                @focus="ensureConditionSuggestionsLoaded(row, condition)"
                @update:model-value="markBulkRowsDirty"
              />
            </v-col>

            <v-col class="d-flex align-center justify-end" cols="12" md="1">
              <v-btn
                icon="mdi-close"
                size="x-small"
                variant="text"
                color="error"
                :disabled="row.conditions.length <= 1 || addSaving"
                :aria-label="$t('ncrm.leads.actions.resetFilters')"
                @click="removeConditionRow(row, conditionIndex)"
              />
            </v-col>
          </v-row>
        </v-card>
      </div>

      <v-alert
        v-if="addValidation && addValidation.valid"
        type="success"
        density="comfortable"
        class="mt-3"
      >
        {{ $t("ncrm.leads.addDialog.validationSuccess", { count: addValidation.valid_rows || 0 }) }}
      </v-alert>

      <v-alert
        v-if="addValidation && addValidation.valid && hasValidationSummary"
        :type="validationSummaryType"
        density="comfortable"
        class="mt-3"
      >
        <div class="text-body-2 mb-2">{{ $t("ncrm.leads.addDialog.validationSummaryTitle") }}</div>
        <div class="text-body-2">
          {{ $t("ncrm.leads.addDialog.validationSummaryMessage", {
            addable: validationAddableCount,
            existing: validationExistingCount,
            duplicates: validationDuplicateCount,
          }) }}
        </div>
      </v-alert>

      <v-alert
        v-if="validationErrors.length"
        type="error"
        density="comfortable"
        class="mt-3"
      >
        <div class="text-body-2 mb-1">{{ $t("ncrm.leads.addDialog.validationErrorsTitle") }}</div>
        <ul class="ncrm-lead-board__validation-list">
          <li v-for="(error, index) in validationErrors" :key="`bulk-error-${index}`">
            {{ formatValidationError(error) }}
          </li>
        </ul>
      </v-alert>

      <div v-if="addPreviewItems.length" class="mt-3">
        <div class="text-subtitle-2 ncrm-text-strong mb-2">
          {{ $t("ncrm.leads.addDialog.previewTitle", { count: addPreviewItems.length }) }}
        </div>
        <v-data-table
          :headers="addPreviewHeaders"
          :items="addPreviewItems"
          class="elevation-0 ncrm-lead-board__preview-table"
          density="comfortable"
          :items-per-page="10"
          :row-props="previewRowProps"
        >
          <template #item.row_index="{ item }">
            <div :class="previewRowClass(item)">
              {{ $t("ncrm.leads.addDialog.validationRowLabel", { index: item.row_index }) }}
            </div>
          </template>
          <template #item.entity_type="{ item }">
            <div class="text-body-2" :class="previewRowClass(item)">
              <div class="ncrm-text-strong">{{ item.entity_label }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ entityTypeLabel(item.entity_type) }}
              </div>
            </div>
          </template>
          <template #item.preview_state="{ item }">
            <v-chip
              variant="flat"
              class="ncrm-lead-board__state-chip"
              :class="previewStateClass(item)"
            >
              <v-icon start size="14">
                {{ previewStateIcon(item) }}
              </v-icon>
              {{ previewStateLabel(item) }}
            </v-chip>
          </template>
          <template #item.owner_name="{ item }">
            <div :class="previewRowClass(item)">
              {{ item.owner_name || "-" }}
            </div>
          </template>
          <template #item.stage_name="{ item }">
            <div :class="previewRowClass(item)">
              {{ item.stage_name || "-" }}
            </div>
          </template>
          <template #item.next_followup_at="{ item }">
            <div :class="previewRowClass(item)">
              {{ formatDateTime(item.next_followup_at) }}
            </div>
          </template>
        </v-data-table>
      </div>
    </NcrmRecordEditDialog>

    <NcrmLeadBoardWizard
      v-model="boardSettingsDialog"
      :title="$t('ncrm.leads.wizard.settingsTitle')"
      :subtitle="$t('ncrm.leads.wizard.settingsSubtitle')"
      :loading="boardSettingsSaving"
      :extra-action-label="$t('ncrm.leads.board.delete.action')"
      extra-action-color="error"
      :extra-action-disabled="boardSettingsSaving || deleteBoardLoading"
      :board="board"
      :stages="stages"
      :show-auto-convert-settings="true"
      :deal-pipelines="dealPipelines"
      @extra-action="openDeleteBoardDialog"
      @submit="saveBoardSettings"
    />

    <NcrmLeadConvertDialog
      v-model="moveConvertDialog.open"
      :title="$t('ncrm.leads.details.dialogs.convert.title')"
      :loading="moveConvertDialog.loading"
      :save-disabled="!canSaveMoveConvertAction"
      :save-label="$t('ncrm.leads.details.actions.convertToDeal')"
      :deal-name="moveConvertDialog.dealName"
      :deal-pipeline-id="moveConvertDialog.dealPipelineId"
      :deal-stage-id="moveConvertDialog.dealStageId"
      :lead-stage-id="moveConvertDialog.leadStageId"
      :owner-user-id="moveConvertDialog.ownerUserId"
      :note="moveConvertDialog.note"
      :pipeline-options="quickDealPipelineOptions"
      :deal-stage-options="quickDealStageOptions"
      :lead-stage-options="quickConvertStageOptions"
      :owner-options="quickOwnerOptions"
      :select-menu-props="selectMenuProps"
      @update:deal-name="moveConvertDialog.dealName = $event"
      @update:deal-pipeline-id="moveConvertDialog.dealPipelineId = $event"
      @update:deal-stage-id="moveConvertDialog.dealStageId = $event"
      @update:lead-stage-id="moveConvertDialog.leadStageId = $event"
      @update:owner-user-id="moveConvertDialog.ownerUserId = $event"
      @update:note="moveConvertDialog.note = $event"
      @pipeline-change="onMoveConvertPipelineChanged"
      @save="submitMoveConvert"
    />

    <NcrmOwnerClaimDialog
      :model-value="ownerClaimDialog.open"
      :loading="ownerClaimDialog.loading"
      :title="$t('ncrm.leads.ownerClaimDialog.title')"
      :subtitle="$t('ncrm.leads.ownerClaimDialog.subtitle')"
      :message="$t('ncrm.leads.ownerClaimDialog.message')"
      :record-field-label="$t('ncrm.leads.ownerClaimDialog.fields.record')"
      :entity-name="ownerClaimDialog.entryName"
      :owner-field-label="$t('ncrm.leads.ownerClaimDialog.fields.currentOwner')"
      :current-owner-label="ownerClaimDialog.currentOwnerLabel"
      :empty-value-label="$t('ncrm.contacts.details.propertyEmpty.notSet')"
      :save-label="$t('ncrm.leads.ownerClaimDialog.actions.confirm')"
      @update:model-value="onOwnerClaimDialogModelUpdate"
      @confirm="confirmOwnerClaim"
    />

    <NcrmRecordEditDialog
      v-model="cardLayoutDialog"
      :title="$t('ncrm.leads.cardLayout.title')"
      :subtitle="$t('ncrm.leads.cardLayout.subtitle')"
      :loading="cardLayoutSaving"
      :save-disabled="cardLayoutSaving || cardLayoutLoading"
      :max-width="980"
      @save="saveCardLayoutSettings"
    >
      <v-row>
        <v-col cols="12" md="5">
          <v-select
            v-model="cardLayoutScope"
            :label="$t('ncrm.leads.cardLayout.fields.scope')"
            :items="cardLayoutScopeOptions"
            item-title="label"
            item-value="value"
            :menu-props="selectMenuProps"
            @update:model-value="syncCardLayoutDraftByScope"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="cardLayoutDraft.density"
            :label="$t('ncrm.leads.cardLayout.fields.density')"
            :items="densityOptions"
            item-title="label"
            item-value="value"
            :menu-props="selectMenuProps"
          />
        </v-col>
        <v-col
          cols="12"
          md="12"
          lg="3"
          class="d-flex align-center justify-end ncrm-lead-board__reset-user-col"
        >
          <v-btn
            v-if="cardLayoutScope === 'user'"
            variant="text"
            color="primary"
            class="text-none ncrm-lead-board__reset-user-btn"
            :disabled="cardLayoutLoading || cardLayoutSaving"
            @click="resetUserCardLayout"
          >
            <v-icon start>mdi-restore</v-icon>
            {{ $t("ncrm.leads.cardLayout.actions.resetUser") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-divider class="my-2" />

      <v-row>
        <v-col cols="12" md="6">
          <div class="d-flex align-center justify-space-between ga-2 mb-2">
            <div class="text-subtitle-2 ncrm-text-strong">
              {{ $t("ncrm.leads.cardLayout.sections.company") }}
            </div>
            <v-btn
              size="x-small"
              variant="tonal"
              color="primary"
              prepend-icon="mdi-plus"
              :disabled="cardLayoutSaving"
              @click="addCardField('company')"
            >
              {{ $t("ncrm.leads.cardLayout.actions.addField") }}
            </v-btn>
          </div>
          <div class="ncrm-lead-board__card-fields">
            <div
              v-for="(fieldKey, index) in cardLayoutDraft.company_fields"
              :key="`card-company-${index}`"
              class="ncrm-lead-board__card-field-row"
            >
              <v-select
                :model-value="fieldKey"
                :items="cardFieldOptionsByEntity.company"
                item-title="label"
                item-value="key"
                :menu-props="selectMenuProps"
                density="compact"
                hide-details
                @update:model-value="(value) => updateCardFieldKey('company', index, value)"
              />
              <v-btn
                icon="mdi-arrow-up"
                size="x-small"
                variant="text"
                color="primary"
                :disabled="index === 0 || cardLayoutSaving"
                @click="moveCardField('company', index, -1)"
              />
              <v-btn
                icon="mdi-arrow-down"
                size="x-small"
                variant="text"
                color="primary"
                :disabled="index >= cardLayoutDraft.company_fields.length - 1 || cardLayoutSaving"
                @click="moveCardField('company', index, 1)"
              />
              <v-btn
                icon="mdi-delete-outline"
                size="x-small"
                variant="text"
                color="error"
                :disabled="cardLayoutDraft.company_fields.length <= 1 || cardLayoutSaving"
                @click="removeCardField('company', index)"
              />
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <div class="d-flex align-center justify-space-between ga-2 mb-2">
            <div class="text-subtitle-2 ncrm-text-strong">
              {{ $t("ncrm.leads.cardLayout.sections.person") }}
            </div>
            <v-btn
              size="x-small"
              variant="tonal"
              color="primary"
              prepend-icon="mdi-plus"
              :disabled="cardLayoutSaving"
              @click="addCardField('person')"
            >
              {{ $t("ncrm.leads.cardLayout.actions.addField") }}
            </v-btn>
          </div>
          <div class="ncrm-lead-board__card-fields">
            <div
              v-for="(fieldKey, index) in cardLayoutDraft.person_fields"
              :key="`card-person-${index}`"
              class="ncrm-lead-board__card-field-row"
            >
              <v-select
                :model-value="fieldKey"
                :items="cardFieldOptionsByEntity.person"
                item-title="label"
                item-value="key"
                :menu-props="selectMenuProps"
                density="compact"
                hide-details
                @update:model-value="(value) => updateCardFieldKey('person', index, value)"
              />
              <v-btn
                icon="mdi-arrow-up"
                size="x-small"
                variant="text"
                color="primary"
                :disabled="index === 0 || cardLayoutSaving"
                @click="moveCardField('person', index, -1)"
              />
              <v-btn
                icon="mdi-arrow-down"
                size="x-small"
                variant="text"
                color="primary"
                :disabled="index >= cardLayoutDraft.person_fields.length - 1 || cardLayoutSaving"
                @click="moveCardField('person', index, 1)"
              />
              <v-btn
                icon="mdi-delete-outline"
                size="x-small"
                variant="text"
                color="error"
                :disabled="cardLayoutDraft.person_fields.length <= 1 || cardLayoutSaving"
                @click="removeCardField('person', index)"
              />
            </div>
          </div>
        </v-col>
      </v-row>
    </NcrmRecordEditDialog>

    <NcrmRecordDeleteDialog
      v-model="deleteBoardDialog"
      :title="$t('ncrm.leads.board.delete.title')"
      :hint="$t('ncrm.leads.board.delete.hint')"
      :confirm-label="$t('ncrm.leads.board.delete.confirmLabel')"
      :action-text="$t('ncrm.leads.board.delete.confirmAction')"
      :confirm-text="deleteBoardConfirmText"
      :loading="deleteBoardLoading"
      @update:confirm-text="deleteBoardConfirmText = $event"
      @confirm="confirmDeleteBoard"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3400">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import NcrmDateTimeField from "@/components/ncrm/shared/NcrmDateTimeField.vue";
import NcrmLastCallCell from "@/components/ncrm/shared/NcrmLastCallCell.vue";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import NcrmRecordDeleteDialog from "@/components/ncrm/shared/NcrmRecordDeleteDialog.vue";
import NcrmOwnerClaimDialog from "@/components/ncrm/shared/NcrmOwnerClaimDialog.vue";
import NcrmLeadBoardWizard from "@/components/ncrm/leads/NcrmLeadBoardWizard.vue";
import NcrmLeadConvertDialog from "@/components/ncrm/leads/NcrmLeadConvertDialog.vue";
import NcrmLeadBoardFiltersDrawer from "@/components/ncrm/leads/NcrmLeadBoardFiltersDrawer.vue";
import NcrmLeadBoardToolbar from "@/components/ncrm/leads/NcrmLeadBoardToolbar.vue";
import {
  loadPreferredView,
  resolveViewPreferencePageKey,
  savePreferredView,
} from "@/Utile/ncrmViewPreferences.mjs";
import { resolveCurrentUserId } from "@/Utile/ncrmCurrentUser.mjs";
import {
  clearLeadBoardFilters,
  loadLeadBoardFilters,
  normalizeLeadBoardFilterConfig,
  saveLeadBoardFilters,
  serializeLeadBoardFilters,
} from "@/Utile/ncrmLeadBoardFilters.mjs";
import {
  cloneNcrmLeadDefaultStages,
  formatNcrmLeadDateTime,
  normalizeNcrmLeadEntryEntityType,
  normalizeNcrmLeadEntityScope,
  normalizeNcrmLeadEntryPriority,
  normalizeNcrmLeadEntryStatus,
  normalizeNcrmLeadString,
  resolveNcrmLeadEntryDisplayName,
  resolveNcrmLeadEntryPriorityColor,
  shouldShowNcrmLeadEntryPriority,
} from "@/Utile/ncrmLeads.mjs";
import { resolveLeadTerminalTaskClosureConfirmation } from "@/Utile/ncrmTerminalTaskClosure.mjs";
import {
  createEmptyOwnerClaimDialog,
  dedupeFieldKeys,
  dedupeLeadFilterFieldItems,
  DEFAULT_VIEW,
  ensureArray,
  LEAD_BOARD_PAGE_KEY_FALLBACK,
  LEAD_BOARD_VIEW_ITEMS,
  LEAD_CARD_DEFAULT_LAYOUT,
  LEAD_DENSITY_ORDER,
  LEAD_STAGE_VISIBLE_BATCH,
  buildLeadBoardEntryFieldLabel,
  buildLeadBoardNextActivityInlineLabel,
  resolveLeadBoardEntityTypeLabel,
  resolveLeadBoardStatusColor,
  resolveLeadBoardStatusLabel,
  buildLeadBoardTableHeaders,
  normalizeLeadBoardDensity,
  normalizeLeadTableSortKey,
  normalizeLeadTableSortOrder,
  parsePositiveId,
  resolveLeadBoardOwnerLabel,
  isLeadBoardDueDateOverdue,
  toUid,
} from "./leadBoardShared.mjs";

export default {
  name: "NcrmLeadBoard",
  components: {
    NcrmDateTimeField,
    NcrmLastCallCell,
    NcrmRecordEditDialog,
    NcrmRecordDeleteDialog,
    NcrmOwnerClaimDialog,
    NcrmLeadBoardWizard,
    NcrmLeadConvertDialog,
    NcrmLeadBoardFiltersDrawer,
    NcrmLeadBoardToolbar,
  },
  data() {
    const initialBoardId = parsePositiveId(this.$route?.params?.id);
    const preferredView =
      loadPreferredView({
        pageKey: resolveViewPreferencePageKey(this.$route, LEAD_BOARD_PAGE_KEY_FALLBACK),
        items: LEAD_BOARD_VIEW_ITEMS,
        fallback: DEFAULT_VIEW,
      }) || DEFAULT_VIEW;
    return {
      boardLoading: false,
      board: null,
      stages: [],
      entries: [],
      byStage: [],
      stageVisibleCounts: {},
      entriesLoading: false,
      entryMoveLoadingId: null,
      draggingEntryId: null,
      dragSourceStageId: null,
      dragOverStageId: null,
      dragClickGuardUntil: 0,
      activeView: String(this.$route?.query?.view || "").trim().toLowerCase() === "table" ? "table" : preferredView,
      preferredView,
      search: "",
      searchDebounceId: null,
      page: 1,
      itemsPerPage: 25,
      sortBy: [],
      totalItems: 0,
      advancedFiltersDialog: false,
      advancedFilters: loadLeadBoardFilters({ boardId: initialBoardId }),
      addDialog: false,
      addRows: [],
      addValidating: false,
      addSaving: false,
      addValidation: null,
      ownerOptions: [],
      ownerOptionsLoading: false,
      dealPipelines: [],
      dealPipelineRequestSeq: 0,
      ownerSearchDebounceId: null,
      ownerRequestSeq: 0,
      filterFieldsByEntity: {
        company: [],
        person: [],
      },
      filterFieldsLoadingByEntity: {
        company: false,
        person: false,
      },
      filterFieldsRequestSeqByEntity: {
        company: 0,
        person: 0,
      },
      conditionSeq: 0,
      currentUserId: null,
      boardSettingsDialog: false,
      boardSettingsSaving: false,
      restoreBoardSettingsAfterDeleteDialog: false,
      deleteBoardDialog: false,
      deleteBoardConfirmText: "",
      deleteBoardLoading: false,
      deleteDialogOpenTimerId: null,
      moveConvertDialog: {
        open: false,
        loading: false,
        entryId: null,
        dealName: "",
        dealPipelineId: null,
        dealStageId: null,
        leadStageId: null,
        ownerUserId: null,
        note: "",
      },
      ownerClaimDialog: createEmptyOwnerClaimDialog(),
      cardLayoutDialog: false,
      cardLayoutLoading: false,
      cardLayoutSaving: false,
      cardLayoutScope: "user",
      cardLayoutConfig: {
        available_fields: {
          company: [],
          person: [],
        },
        default_layout: {
          ...LEAD_CARD_DEFAULT_LAYOUT,
          company_fields: [...LEAD_CARD_DEFAULT_LAYOUT.company_fields],
          person_fields: [...LEAD_CARD_DEFAULT_LAYOUT.person_fields],
        },
        board_layout: null,
        user_layout: null,
        effective_layout: {
          ...LEAD_CARD_DEFAULT_LAYOUT,
          company_fields: [...LEAD_CARD_DEFAULT_LAYOUT.company_fields],
          person_fields: [...LEAD_CARD_DEFAULT_LAYOUT.person_fields],
        },
      },
      cardLayoutDraft: {
        density: LEAD_CARD_DEFAULT_LAYOUT.density,
        company_fields: [...LEAD_CARD_DEFAULT_LAYOUT.company_fields],
        person_fields: [...LEAD_CARD_DEFAULT_LAYOUT.person_fields],
      },
      effectiveDensity: LEAD_CARD_DEFAULT_LAYOUT.density,
      snackbar: {
        show: false,
        color: "info",
        message: "",
      },
    };
  },
  computed: {
    boardId() {
      return parsePositiveId(this.$route?.params?.id);
    },
    viewItems() {
      return [
        { value: "board", label: this.$t("ncrm.leads.views.board") },
        { value: "table", label: this.$t("ncrm.leads.views.table") },
      ];
    },
    tableHeaders() {
      return buildLeadBoardTableHeaders((key) => this.$t(key));
    },
    addPreviewHeaders() {
      return [
        { title: this.$t("ncrm.leads.addDialog.previewHeaders.row"), key: "row_index" },
        { title: this.$t("ncrm.leads.addDialog.previewHeaders.entity"), key: "entity_type" },
        { title: this.$t("ncrm.leads.addDialog.previewHeaders.state"), key: "preview_state" },
        { title: this.$t("ncrm.leads.addDialog.previewHeaders.owner"), key: "owner_name" },
        { title: this.$t("ncrm.leads.addDialog.previewHeaders.stage"), key: "stage_name" },
        { title: this.$t("ncrm.leads.addDialog.previewHeaders.nextFollowup"), key: "next_followup_at" },
      ];
    },
    conditionOperatorOptions() {
      return [
        { value: "equals", label: this.$t("ncrm.leads.addDialog.operators.equals") },
        { value: "not_equals", label: this.$t("ncrm.leads.addDialog.operators.notEquals") },
        { value: "contains", label: this.$t("ncrm.leads.addDialog.operators.contains") },
        { value: "not_contains", label: this.$t("ncrm.leads.addDialog.operators.notContains") },
        { value: "is_empty", label: this.$t("ncrm.leads.addDialog.operators.isEmpty") },
        { value: "is_not_empty", label: this.$t("ncrm.leads.addDialog.operators.isNotEmpty") },
      ];
    },
    conditionLogicOptions() {
      return [
        { value: "and", label: this.$t("ncrm.leads.addDialog.logic.and") },
        { value: "or", label: this.$t("ncrm.leads.addDialog.logic.or") },
      ];
    },
    densityOptions() {
      return [
        { value: "comfortable", label: this.$t("ncrm.leads.board.density.comfortable") },
        { value: "standard", label: this.$t("ncrm.leads.board.density.standard") },
        { value: "compact", label: this.$t("ncrm.leads.board.density.compact") },
        { value: "micro", label: this.$t("ncrm.leads.board.density.micro") },
      ];
    },
    densityLabelMap() {
      return this.densityOptions.reduce((acc, option) => {
        if (!option?.value) return acc;
        acc[option.value] = option.label;
        return acc;
      }, {});
    },
    currentDensityLabel() {
      return this.densityLabelMap[this.effectiveDensity] || this.densityLabelMap.comfortable || "Comfortable";
    },
    nextDensity() {
      const currentIndex = LEAD_DENSITY_ORDER.indexOf(this.effectiveDensity);
      const startIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextIndex = (startIndex + 1) % LEAD_DENSITY_ORDER.length;
      return LEAD_DENSITY_ORDER[nextIndex];
    },
    nextDensityLabel() {
      return this.densityLabelMap[this.nextDensity] || this.nextDensity;
    },
    densityStateIcon() {
      const iconMap = {
        comfortable: "mdi-view-day-outline",
        standard: "mdi-view-agenda-outline",
        compact: "mdi-view-stream-outline",
        micro: "mdi-view-column-outline",
      };
      return iconMap[this.effectiveDensity] || iconMap.comfortable;
    },
    densityCycleTooltip() {
      return this.$t("ncrm.leads.board.densityCycleTooltip", {
        current: this.currentDensityLabel,
        next: this.nextDensityLabel,
      });
    },
    densityCycleAriaLabel() {
      return this.$t("ncrm.leads.board.densityCycleAria", {
        current: this.currentDensityLabel,
        next: this.nextDensityLabel,
      });
    },
    densityCycleDisabled() {
      return Boolean(this.cardLayoutLoading || this.cardLayoutSaving || !this.boardId);
    },
    entryCardPaddingClass() {
      if (this.effectiveDensity === "micro") return "pa-1";
      if (this.effectiveDensity === "compact") return "pa-1";
      if (this.effectiveDensity === "standard") return "pa-2";
      return "pa-2";
    },
    cardLayoutScopeOptions() {
      return [
        { value: "user", label: this.$t("ncrm.leads.cardLayout.scope.user") },
        { value: "board", label: this.$t("ncrm.leads.cardLayout.scope.board") },
      ];
    },
    cardFieldOptionsByEntity() {
      const company = ensureArray(this.cardLayoutConfig?.available_fields?.company)
        .map((entry) => ({
          key: normalizeNcrmLeadString(entry?.key),
          label: normalizeNcrmLeadString(entry?.label) || normalizeNcrmLeadString(entry?.key),
        }))
        .filter((entry) => entry.key);
      const person = ensureArray(this.cardLayoutConfig?.available_fields?.person)
        .map((entry) => ({
          key: normalizeNcrmLeadString(entry?.key),
          label: normalizeNcrmLeadString(entry?.label) || normalizeNcrmLeadString(entry?.key),
        }))
        .filter((entry) => entry.key);
      return { company, person };
    },
    effectiveCardLayout() {
      const fallback = {
        density: LEAD_CARD_DEFAULT_LAYOUT.density,
        company_fields: [...LEAD_CARD_DEFAULT_LAYOUT.company_fields],
        person_fields: [...LEAD_CARD_DEFAULT_LAYOUT.person_fields],
      };
      const source = this.cardLayoutConfig?.effective_layout || fallback;
      return {
        density: normalizeLeadBoardDensity(source?.density, fallback.density),
        company_fields: dedupeFieldKeys(source?.company_fields || source?.companyFields || fallback.company_fields),
        person_fields: dedupeFieldKeys(source?.person_fields || source?.personFields || fallback.person_fields),
      };
    },
    boardColumns() {
      const source = Array.isArray(this.byStage) && this.byStage.length
        ? this.byStage
        : (this.stages || []).map((stage) => ({
            stage_id: stage.id,
            stage_name: stage.name,
            stage_key: stage.stage_key,
            stage_type: stage.stage_type,
            is_default: stage.is_default,
            is_active: stage.is_active,
            items: [],
          }));
      return source
        .map((stage) => {
          const stageId = parsePositiveId(stage?.stage_id ?? stage?.id);
          const items = Array.isArray(stage?.items) ? stage.items : [];
          const visibleLimit = this.resolveStageVisibleCount(stageId);
          const visibleItems = items.slice(0, visibleLimit);
          return {
            ...stage,
            stage_id: stageId || stage?.stage_id || stage?.id,
            items,
            visible_items: visibleItems,
            visible_count: visibleItems.length,
            remaining_count: Math.max(items.length - visibleItems.length, 0),
            has_more: items.length > visibleItems.length,
          };
        })
        .filter((stage) => Number(stage?.is_active ?? 1) === 1 || (stage?.items || []).length > 0);
    },
    stageOptions() {
      return (this.stages || [])
        .filter((stage) => Number(stage?.is_active ?? 1) === 1)
        .map((stage) => ({
          id: Number(stage.id),
          label: stage.name,
          stage_type: normalizeNcrmLeadString(stage?.stage_type).toLowerCase() || "open",
        }));
    },
    quickConvertStageOptions() {
      const conversionOnly = this.stageOptions.filter((stage) => stage.stage_type === "conversion");
      return conversionOnly.length ? conversionOnly : this.stageOptions;
    },
    quickDealPipelineOptions() {
      return (this.dealPipelines || [])
        .filter((pipeline) => Number(pipeline?.is_active) === 1 || pipeline?.is_active === true)
        .map((pipeline) => ({
          id: parsePositiveId(pipeline?.id),
          label: normalizeNcrmLeadString(pipeline?.name) || `#${parsePositiveId(pipeline?.id) || "-"}`,
        }))
        .filter((item) => item.id);
    },
    selectedMoveConvertPipelineId() {
      const selected = parsePositiveId(this.moveConvertDialog.dealPipelineId);
      if (selected && this.quickDealPipelineOptions.some((option) => option.id === selected)) {
        return selected;
      }
      return this.quickDealPipelineOptions[0]?.id || null;
    },
    quickDealStageOptions() {
      const pipelineId = parsePositiveId(this.selectedMoveConvertPipelineId);
      if (!pipelineId) return [];
      const pipeline = (this.dealPipelines || []).find((entry) => parsePositiveId(entry?.id) === pipelineId);
      if (!pipeline) return [];
      return (Array.isArray(pipeline?.stages) ? pipeline.stages : [])
        .filter((stage) => Number(stage?.is_active) === 1 || stage?.is_active === true)
        .map((stage) => ({
          id: parsePositiveId(stage?.id),
          label: normalizeNcrmLeadString(stage?.name) || `#${parsePositiveId(stage?.id) || "-"}`,
        }))
        .filter((item) => item.id);
    },
    quickOwnerOptions() {
      return (this.ownerOptions || [])
        .map((owner) => ({
          id: parsePositiveId(owner?.id),
          label: normalizeNcrmLeadString(owner?.label) || `#${parsePositiveId(owner?.id) || "-"}`,
        }))
        .filter((owner) => owner.id);
    },
    defaultStageId() {
      const defaultStage = (this.stages || []).find((stage) => Number(stage?.is_default || 0) === 1);
      if (defaultStage?.id) return Number(defaultStage.id);
      const firstStage = (this.stages || []).find((stage) => Number(stage?.is_active ?? 1) === 1);
      return Number(firstStage?.id || 0) || null;
    },
    boardEntityScope() {
      return normalizeNcrmLeadEntityScope(this.board?.entity_scope, "both") || "both";
    },
    isEntityTypeLocked() {
      return this.boardEntityScope !== "both";
    },
    entityTypeOptions() {
      const all = [
        { value: "company", label: this.$t("ncrm.leads.entityTypes.company") },
        { value: "person", label: this.$t("ncrm.leads.entityTypes.person") },
      ];
      if (this.boardEntityScope === "company_only") return all.filter((item) => item.value === "company");
      if (this.boardEntityScope === "person_only") return all.filter((item) => item.value === "person");
      return all;
    },
    totalOpenEntries() {
      return (this.entries || []).filter((entry) => normalizeNcrmLeadEntryStatus(entry?.status, "open") === "open")
        .length;
    },
    validationErrors() {
      return Array.isArray(this.addValidation?.errors) ? this.addValidation.errors : [];
    },
    addPreviewItems() {
      return Array.isArray(this.addValidation?.preview_items) ? this.addValidation.preview_items : [];
    },
    validationAddableCount() {
      return Number(this.addValidation?.valid_rows || 0);
    },
    validationExistingCount() {
      return Number(this.addValidation?.skipped_existing || 0);
    },
    validationDuplicateCount() {
      return Number(this.addValidation?.skipped_duplicate || 0);
    },
    hasValidationSummary() {
      return this.validationAddableCount > 0 || this.validationExistingCount > 0 || this.validationDuplicateCount > 0;
    },
    validationSummaryType() {
      if (this.validationAddableCount > 0) return "info";
      return "warning";
    },
    addSaveDisabled() {
      if (this.addSaving || this.addValidating) return true;
      if (!this.addRows.length) return true;
      if (!this.addValidation || !this.addValidation.valid) return true;
      if (Number(this.addValidation?.valid_rows || 0) <= 0) return true;
      return false;
    },
    selectMenuProps() {
      return {
        maxHeight: 320,
        contentClass: "ncrm-lead-board-select-menu",
        zIndex: 2710,
      };
    },
    canSaveMoveConvertAction() {
      if (this.moveConvertDialog.loading) return false;
      if (!parsePositiveId(this.moveConvertDialog.entryId)) return false;
      const pipelineId = parsePositiveId(this.moveConvertDialog.dealPipelineId || this.selectedMoveConvertPipelineId);
      const stageId = parsePositiveId(this.moveConvertDialog.dealStageId);
      return Boolean(pipelineId && stageId);
    },
  },
  watch: {
    "$route.params.id"() {
      this.restorePreferredView();
      this.restoreBoardFilters();
      this.initializeBoard();
    },
    "$route.query.view"(value) {
      const changed = this.applyRouteView(value, false);
      if (changed) {
        this.page = 1;
        this.loadEntries();
      }
    },
    activeView(value) {
      this.syncRouteView(value);
      this.page = 1;
      this.loadEntries();
    },
    page() {
      if (this.activeView !== "table") return;
      this.loadEntries();
    },
    itemsPerPage() {
      if (this.activeView !== "table") return;
      this.page = 1;
      this.loadEntries();
    },
    sortBy: {
      deep: true,
      handler() {
        if (this.activeView !== "table") return;
        if (this.page !== 1) {
          this.page = 1;
          return;
        }
        this.loadEntries();
      },
    },
    search() {
      if (this.searchDebounceId) {
        clearTimeout(this.searchDebounceId);
      }
      this.searchDebounceId = setTimeout(() => {
        this.page = 1;
        this.loadEntries();
      }, 260);
    },
    async effectiveDensity(value, previous) {
      if (value === previous) return;
      if (!this.boardId) return;
      if (this.cardLayoutLoading || this.cardLayoutSaving) return;
      await this.saveDensityPreference(value);
    },
    deleteBoardDialog(value) {
      if (value || this.deleteBoardLoading) return;
      if (!this.restoreBoardSettingsAfterDeleteDialog) return;
      this.restoreBoardSettingsAfterDeleteDialog = false;
      this.boardSettingsDialog = true;
    },
  },
  mounted() {
    this.syncRouteView(this.activeView);
    this.initializeBoard();
  },
  beforeUnmount() {
    ["searchDebounceId", "ownerSearchDebounceId", "deleteDialogOpenTimerId"].forEach((key) => {
      if (this[key]) {
        clearTimeout(this[key]);
        this[key] = null;
      }
    });
    this.clearConditionTimers();
  },
  methods: {
    viewPreferencePageKey() {
      return resolveViewPreferencePageKey(this.$route, LEAD_BOARD_PAGE_KEY_FALLBACK);
    },
    restoreBoardFilters() {
      this.advancedFiltersDialog = false;
      this.advancedFilters = loadLeadBoardFilters({ boardId: this.boardId });
    },
    persistBoardFilters(filters) {
      const normalized = saveLeadBoardFilters({
        boardId: this.boardId,
        filters: normalizeLeadBoardFilterConfig(filters),
      });
      this.advancedFilters = normalized;
      return normalized;
    },
    clearAdvancedFilters({ reload = true } = {}) {
      clearLeadBoardFilters({ boardId: this.boardId });
      this.advancedFilters = normalizeLeadBoardFilterConfig();
      if (reload) {
        this.page = 1;
        this.loadEntries();
      }
    },
    applyAdvancedFilters(filters) {
      this.persistBoardFilters(filters);
      this.page = 1;
      this.loadEntries();
    },
    removeAdvancedFilter(index) {
      const nextRules = (Array.isArray(this.advancedFilters?.rules) ? this.advancedFilters.rules : []).filter(
        (_rule, ruleIndex) => ruleIndex !== index
      );
      if (!nextRules.length) {
        this.clearAdvancedFilters({ reload: true });
        return;
      }
      this.persistBoardFilters({
        logic: this.advancedFilters?.logic || "and",
        rules: nextRules,
      });
      this.page = 1;
      this.loadEntries();
    },
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        color,
        message,
      };
    },
    formatDateTime(value) {
      return formatNcrmLeadDateTime(value, this.$i18n?.locale || "ro-RO");
    },
    resolveStageVisibleCount(stageId) {
      const normalizedId = parsePositiveId(stageId);
      if (!normalizedId) return LEAD_STAGE_VISIBLE_BATCH;
      const current = Number(this.stageVisibleCounts?.[normalizedId]);
      if (!Number.isFinite(current) || current <= 0) return LEAD_STAGE_VISIBLE_BATCH;
      return Math.trunc(current);
    },
    stageCountLabel(column = {}) {
      const total = Array.isArray(column?.items) ? column.items.length : 0;
      const shown = Number(column?.visible_count || 0);
      if (total > shown) {
        return this.$t("ncrm.leads.board.showingCount", { shown, total });
      }
      return String(total);
    },
    stageLoadMoreLabel(column = {}) {
      const remaining = Number(column?.remaining_count || 0);
      const nextBatch = Math.min(remaining, LEAD_STAGE_VISIBLE_BATCH);
      return this.$t("ncrm.leads.board.loadMore", { count: nextBatch });
    },
    loadMoreStageEntries(stageId) {
      const normalizedId = parsePositiveId(stageId);
      if (!normalizedId) return;
      const current = this.resolveStageVisibleCount(normalizedId);
      this.stageVisibleCounts = {
        ...this.stageVisibleCounts,
        [normalizedId]: current + LEAD_STAGE_VISIBLE_BATCH,
      };
    },
    syncStageVisibleCounts(stages = []) {
      const nextMap = {};
      for (const stage of stages) {
        const stageId = parsePositiveId(stage?.stage_id ?? stage?.id);
        if (!stageId) continue;
        nextMap[stageId] = this.resolveStageVisibleCount(stageId);
      }
      this.stageVisibleCounts = nextMap;
    },
    isEntryDraggable(entry = {}) {
      const entryId = parsePositiveId(entry?.id);
      if (!entryId) return false;
      if (this.entriesLoading) return false;
      if (this.entryMoveLoadingId) return false;
      return true;
    },
    onEntryDragStart(event, entry = {}, column = {}) {
      if (!this.isEntryDraggable(entry)) {
        event?.preventDefault?.();
        return;
      }
      const entryId = parsePositiveId(entry?.id);
      const sourceStageId = parsePositiveId(entry?.stage_id ?? column?.stage_id);
      if (!entryId || !sourceStageId) {
        event?.preventDefault?.();
        return;
      }
      this.draggingEntryId = entryId;
      this.dragSourceStageId = sourceStageId;
      this.dragOverStageId = sourceStageId;
      this.dragClickGuardUntil = Date.now() + 350;
      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.dropEffect = "move";
        event.dataTransfer.setData("text/plain", String(entryId));
      }
    },
    onEntryDragEnd() {
      this.dragClickGuardUntil = Date.now() + 350;
      this.clearDragState();
    },
    clearDragState() {
      this.draggingEntryId = null;
      this.dragSourceStageId = null;
      this.dragOverStageId = null;
    },
    columnDragClasses(column = {}) {
      const stageId = parsePositiveId(column?.stage_id);
      if (!stageId || !this.draggingEntryId) return {};
      if (stageId === this.dragSourceStageId) {
        return { "ncrm-lead-board__column--drag-source": true };
      }
      if (stageId === this.dragOverStageId) {
        return { "ncrm-lead-board__column--drop-target": true };
      }
      return {};
    },
    onStageDragOver(event, column = {}) {
      if (!this.draggingEntryId || this.entryMoveLoadingId) return;
      const stageId = parsePositiveId(column?.stage_id);
      if (!stageId) return;
      this.dragOverStageId = stageId;
      if (event?.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }
    },
    async onStageDrop(column = {}) {
      if (!this.draggingEntryId || this.entryMoveLoadingId) {
        this.clearDragState();
        return;
      }
      const targetStageId = parsePositiveId(column?.stage_id);
      const entryId = parsePositiveId(this.draggingEntryId);
      const sourceStageId = parsePositiveId(this.dragSourceStageId);
      if (!entryId || !targetStageId) {
        this.clearDragState();
        return;
      }
      if (sourceStageId === targetStageId) {
        this.clearDragState();
        return;
      }
      await this.moveEntryToStage(entryId, targetStageId);
      this.clearDragState();
    },
    entityScopeLabel(scope) {
      const normalized = normalizeNcrmLeadEntityScope(scope, "both") || "both";
      return this.$t(`ncrm.leads.entityScope.${normalized}`);
    },
    entityTypeLabel(type) {
      return resolveLeadBoardEntityTypeLabel(type, this.$t);
    },
    ownerLabel(entry = {}) {
      return resolveLeadBoardOwnerLabel(entry);
    },
    tableRowProps() {
      return { style: "cursor: pointer" };
    },
    columnAccentColor(index) {
      const colors = ["primary", "info", "success", "warning", "tertiary", "secondary"];
      return colors[index % colors.length];
    },
    columnAccentStyle(index) {
      const color = this.columnAccentColor(index);
      return `rgb(var(--v-theme-${color}))`;
    },
    ownerOptionLabel(ownerUserId) {
      const normalizedOwnerId = parsePositiveId(ownerUserId);
      if (!normalizedOwnerId) return "-";
      const option = this.quickOwnerOptions.find((entry) => entry.id === normalizedOwnerId);
      return option?.label || `#${normalizedOwnerId}`;
    },
    resolveEntrySnapshot(entryId) {
      const normalizedEntryId = parsePositiveId(entryId);
      if (!normalizedEntryId) return null;
      return (this.entries || []).find((entry) => parsePositiveId(entry?.id) === normalizedEntryId) || null;
    },
    onOwnerClaimDialogModelUpdate(value) {
      if (value) {
        this.ownerClaimDialog.open = true;
        return;
      }
      if (this.ownerClaimDialog.loading) return;
      this.ownerClaimDialog = createEmptyOwnerClaimDialog();
    },
    async maybeOpenOwnerClaimDialog({ entryId, entryName, ownerUserId, ownerLabel } = {}) {
      const currentUserId = await this.fetchCurrentUser();
      const normalizedEntryId = parsePositiveId(entryId);
      const normalizedOwnerId = parsePositiveId(ownerUserId);
      if (!currentUserId || !normalizedEntryId || !normalizedOwnerId || currentUserId === normalizedOwnerId) {
        return;
      }
      this.ownerClaimDialog = {
        open: true,
        loading: false,
        entryId: normalizedEntryId,
        entryName:
          normalizeNcrmLeadString(entryName) || this.$t("ncrm.leads.details.titleFallback", { id: normalizedEntryId }),
        currentOwnerLabel: normalizeNcrmLeadString(ownerLabel) || this.ownerOptionLabel(normalizedOwnerId),
      };
    },
    async confirmOwnerClaim() {
      const entryId = parsePositiveId(this.ownerClaimDialog.entryId);
      const currentUserId = await this.fetchCurrentUser();
      if (!entryId || !currentUserId || this.ownerClaimDialog.loading) return;
      this.ownerClaimDialog.loading = true;
      try {
        await axios.patch(`/ncrm/leads/entries/${entryId}`, {
          owner_user_id: currentUserId,
        });
        this.ownerClaimDialog = createEmptyOwnerClaimDialog();
        await this.loadEntries();
        this.showSnackbar(this.$t("ncrm.leads.ownerClaimDialog.messages.success"), "success");
      } catch (error) {
        console.error("[ncrm-leads] claim owner failed:", error);
        this.showSnackbar(this.$t("ncrm.leads.ownerClaimDialog.messages.error"), "error");
      } finally {
        if (this.ownerClaimDialog.open) {
          this.ownerClaimDialog.loading = false;
        }
      }
    },
    entryDisplayName(entry = {}) {
      return resolveNcrmLeadEntryDisplayName(entry);
    },
    compactEntityCellLabel(entry = {}) {
      const name = this.entryDisplayName(entry);
      const entityType = this.entityTypeLabel(entry?.entity_type);
      return [name, entityType].filter((part) => part && part !== "-").join(" • ") || "-";
    },
    entryStatusLabel(status) {
      return resolveLeadBoardStatusLabel(status, this.$t);
    },
    entryStatusColor(status) {
      return resolveLeadBoardStatusColor(status);
    },
    entryPriorityLabel(priority) {
      return this.$t(`ncrm.leads.priorities.${normalizeNcrmLeadEntryPriority(priority)}`);
    },
    entryPriorityColor(priority) {
      return resolveNcrmLeadEntryPriorityColor(priority);
    },
    shouldShowEntryPriority(priority) {
      return shouldShowNcrmLeadEntryPriority(priority);
    },
    isNextActivityOverdue(value) {
      return isLeadBoardDueDateOverdue(value);
    },
    nextActivityInlineLabel(entry = {}) {
      return buildLeadBoardNextActivityInlineLabel(entry, {
        formatDateTime: this.formatDateTime,
        t: this.$t,
        te: this.$te,
      });
    },
    resolveLeadRoute(entry) {
      const leadId = parsePositiveId(entry?.id);
      if (!leadId) return null;
      const boardId = this.boardId;
      const query = {
        tab: "activity",
      };
      if (boardId) {
        query.boardId = String(boardId);
      }
      return {
        name: "NcrmLeadDetails",
        params: { id: String(leadId) },
        query,
      };
    },
    openLeadDetails(entry) {
      const route = this.resolveLeadRoute(entry);
      if (!route) return;
      this.$router.push(route);
    },
    handleEntryCardActivate(entry) {
      if (!entry) return;
      if (this.entryMoveLoadingId || this.draggingEntryId) return;
      if (Date.now() < Number(this.dragClickGuardUntil || 0)) return;
      this.openLeadDetails(entry);
    },
    handleEntryRowClick(_event, payload) {
      const row = payload?.item?.raw ?? payload?.item;
      this.openLeadDetails(row);
    },
    restorePreferredView() {
      const preferredView =
        loadPreferredView({
          pageKey: this.viewPreferencePageKey(),
          items: LEAD_BOARD_VIEW_ITEMS,
          fallback: DEFAULT_VIEW,
        }) || DEFAULT_VIEW;
      this.preferredView = preferredView;
      const routeView = String(this.$route?.query?.view || "").trim().toLowerCase();
      this.activeView = routeView === "table" ? "table" : preferredView;
    },
    setPreferredView(value) {
      const preferredView = savePreferredView({
        pageKey: this.viewPreferencePageKey(),
        value,
        items: LEAD_BOARD_VIEW_ITEMS,
      });
      if (!preferredView) return;
      this.preferredView = preferredView;
      if (preferredView !== this.activeView) {
        this.activeView = preferredView;
      }
    },
    applyRouteView(value, force = false) {
      const normalized = String(value || "").trim().toLowerCase();
      const nextView =
        normalized === "table"
          ? "table"
          : normalized === "board"
            ? "board"
            : this.preferredView || DEFAULT_VIEW;
      if (!force && nextView === this.activeView) return false;
      this.activeView = nextView;
      return true;
    },
    syncRouteView(view) {
      const normalized = view === "table" ? "table" : "board";
      const current = String(this.$route?.query?.view || "").trim().toLowerCase();
      if (current === normalized) return;
      this.$router.replace({
        query: {
          ...(this.$route?.query || {}),
          view: normalized,
        },
      }).catch(() => {});
    },
    resolveTableSortState() {
      const firstSort = Array.isArray(this.sortBy) && this.sortBy.length ? this.sortBy[0] : null;
      const sortKey = normalizeLeadTableSortKey(firstSort?.key);
      const sortOrder = normalizeLeadTableSortOrder(firstSort?.order);
      if (!sortKey || !sortOrder) return null;
      return { key: sortKey, order: sortOrder };
    },
    resetFilters() {
      if (this.searchDebounceId) {
        clearTimeout(this.searchDebounceId);
        this.searchDebounceId = null;
      }
      this.search = "";
      clearLeadBoardFilters({ boardId: this.boardId });
      this.advancedFilters = normalizeLeadBoardFilterConfig();
      this.page = 1;
      this.itemsPerPage = 25;
      this.loadEntries();
    },
    cycleDensity() {
      if (this.densityCycleDisabled) return;
      this.effectiveDensity = this.nextDensity;
    },
    async openBoardSettings() {
      await Promise.all([this.fetchOwnerOptions(""), this.fetchDealPipelines()]);
      this.boardSettingsDialog = true;
    },
    openDeleteBoardDialog() {
      if (!this.boardId) return;
      this.deleteBoardConfirmText = "";
      const hadBoardSettingsOpen = this.boardSettingsDialog === true;
      this.restoreBoardSettingsAfterDeleteDialog = hadBoardSettingsOpen;
      if (hadBoardSettingsOpen) {
        this.boardSettingsDialog = false;
      }
      if (this.deleteDialogOpenTimerId) {
        clearTimeout(this.deleteDialogOpenTimerId);
      }
      this.deleteDialogOpenTimerId = setTimeout(() => {
        this.deleteBoardDialog = true;
        this.deleteDialogOpenTimerId = null;
      }, hadBoardSettingsOpen ? 120 : 0);
    },
    async confirmDeleteBoard() {
      if (!this.boardId) return;
      this.deleteBoardLoading = true;
      try {
        await axios.delete(`/ncrm/leads/boards/${this.boardId}`);
        this.restoreBoardSettingsAfterDeleteDialog = false;
        this.deleteBoardDialog = false;
        this.boardSettingsDialog = false;
        this.showSnackbar(this.$t("ncrm.leads.board.delete.messages.success"), "success");
        this.$router.replace({ name: "NcrmLeadsHome" }).catch(() => {});
      } catch (error) {
        console.error("[ncrm-leads] delete board failed:", error);
        const apiMessage = normalizeNcrmLeadString(error?.response?.data?.error);
        this.showSnackbar(apiMessage || this.$t("ncrm.leads.board.delete.messages.deleteError"), "error");
      } finally {
        this.deleteBoardLoading = false;
      }
    },
    async saveBoardSettings(payload = {}) {
      if (!this.boardId || !this.board) return;
      this.boardSettingsSaving = true;
      try {
        await axios.patch(`/ncrm/leads/boards/${this.boardId}`, {
          name: normalizeNcrmLeadString(payload?.name),
          entity_scope: normalizeNcrmLeadString(payload?.entity_scope || payload?.entityScope) || this.board.entity_scope,
          auto_convert_on_qualified:
            payload?.auto_convert_on_qualified !== undefined
              ? payload.auto_convert_on_qualified
              : this.board?.auto_convert_on_qualified,
          auto_convert_deal_pipeline_id: parsePositiveId(payload?.auto_convert_deal_pipeline_id) || null,
          auto_convert_deal_stage_id: parsePositiveId(payload?.auto_convert_deal_stage_id) || null,
          auto_create_task_if_missing_after_change: payload?.auto_create_task_if_missing_after_change === true,
          auto_create_task_if_missing_delay_minutes:
            Number.isFinite(Number(payload?.auto_create_task_if_missing_delay_minutes))
              ? Math.trunc(Number(payload.auto_create_task_if_missing_delay_minutes))
              : 60,
        });

        const currentStages = Array.isArray(this.stages) ? [...this.stages] : [];
        const nextStages = Array.isArray(payload?.stages) ? payload.stages : cloneNcrmLeadDefaultStages();
        const nextIds = new Set(
          nextStages.map((stage) => parsePositiveId(stage?.id)).filter(Boolean)
        );

        const toDelete = currentStages.filter((stage) => {
          const stageId = parsePositiveId(stage?.id);
          return stageId && !nextIds.has(stageId);
        });
        for (const stage of toDelete) {
          const stageId = parsePositiveId(stage?.id);
          if (!stageId) continue;
          await axios.delete(`/ncrm/leads/stages/${stageId}`);
        }

        const existingById = new Set(currentStages.map((stage) => parsePositiveId(stage?.id)).filter(Boolean));
        for (let index = 0; index < nextStages.length; index += 1) {
          const stage = nextStages[index];
          const stageId = parsePositiveId(stage?.id);
          const stagePayload = {
            name: normalizeNcrmLeadString(stage?.name),
            stage_key: normalizeNcrmLeadString(stage?.stage_key || stage?.stageKey),
            stage_type: normalizeNcrmLeadString(stage?.stage_type || stage?.stageType) || "open",
            sort_order: index + 1,
            is_default: Boolean(stage?.is_default ?? stage?.isDefault),
          };

          if (stageId && existingById.has(stageId)) {
            await axios.patch(`/ncrm/leads/stages/${stageId}`, stagePayload);
          } else {
            await axios.post(`/ncrm/leads/boards/${this.boardId}/stages`, stagePayload);
          }
        }

        this.boardSettingsDialog = false;
        await this.loadEntries();
        this.showSnackbar(this.$t("ncrm.leads.board.messages.settingsSaved"), "success");
      } catch (error) {
        console.error("[ncrm-leads] save board settings failed:", error);
        const apiMessage = normalizeNcrmLeadString(error?.response?.data?.error);
        this.showSnackbar(apiMessage || this.$t("ncrm.leads.board.messages.settingsSaveError"), "error");
      } finally {
        this.boardSettingsSaving = false;
      }
    },
    async openCardLayoutDialog() {
      await this.loadCardLayoutConfig();
      this.cardLayoutDialog = true;
      this.syncCardLayoutDraftByScope();
    },
    resolveLayoutOrFallback(layout) {
      const fallback = {
        density: LEAD_CARD_DEFAULT_LAYOUT.density,
        company_fields: [...LEAD_CARD_DEFAULT_LAYOUT.company_fields],
        person_fields: [...LEAD_CARD_DEFAULT_LAYOUT.person_fields],
      };
      if (!layout || typeof layout !== "object") return fallback;
      return {
        density: normalizeLeadBoardDensity(layout?.density, fallback.density),
        company_fields: dedupeFieldKeys(layout?.company_fields || layout?.companyFields || fallback.company_fields),
        person_fields: dedupeFieldKeys(layout?.person_fields || layout?.personFields || fallback.person_fields),
      };
    },
    async loadCardLayoutConfig() {
      if (!this.boardId) return;
      this.cardLayoutLoading = true;
      try {
        const { data } = await axios.get(`/ncrm/leads/boards/${this.boardId}/card-layout`);
        const safeData = data || {};
        this.cardLayoutConfig = {
          available_fields: safeData?.available_fields || { company: [], person: [] },
          default_layout: this.resolveLayoutOrFallback(safeData?.default_layout),
          board_layout: safeData?.board_layout ? this.resolveLayoutOrFallback(safeData.board_layout) : null,
          user_layout: safeData?.user_layout ? this.resolveLayoutOrFallback(safeData.user_layout) : null,
          effective_layout: this.resolveLayoutOrFallback(safeData?.effective_layout),
        };
        const nextDensity = normalizeLeadBoardDensity(
          this.cardLayoutConfig.effective_layout?.density,
          LEAD_CARD_DEFAULT_LAYOUT.density
        );
        this.effectiveDensity = nextDensity;
      } catch (error) {
        console.error("[ncrm-leads] load card layout failed:", error);
        this.showSnackbar(this.$t("ncrm.leads.cardLayout.messages.loadError"), "error");
      } finally {
        this.cardLayoutLoading = false;
      }
    },
    syncCardLayoutDraftByScope() {
      const source = this.cardLayoutScope === "board"
        ? this.resolveLayoutOrFallback(this.cardLayoutConfig?.board_layout || this.cardLayoutConfig?.default_layout)
        : this.resolveLayoutOrFallback(this.cardLayoutConfig?.user_layout || this.cardLayoutConfig?.effective_layout);
      this.cardLayoutDraft = {
        density: source.density,
        company_fields: [...source.company_fields],
        person_fields: [...source.person_fields],
      };
    },
    addCardField(entityType) {
      const normalized = normalizeNcrmLeadString(entityType).toLowerCase();
      if (!["company", "person"].includes(normalized)) return;
      const options = this.cardFieldOptionsByEntity[normalized] || [];
      const current = dedupeFieldKeys(this.cardLayoutDraft[`${normalized}_fields`]);
      const nextAvailableKey =
        options.find((entry) => entry?.key && !current.includes(entry.key))?.key || options[0]?.key;
      if (!nextAvailableKey) return;
      current.push(nextAvailableKey);
      this.cardLayoutDraft[`${normalized}_fields`] = dedupeFieldKeys(current);
    },
    removeCardField(entityType, index) {
      const normalized = normalizeNcrmLeadString(entityType).toLowerCase();
      if (!["company", "person"].includes(normalized)) return;
      const current = dedupeFieldKeys(this.cardLayoutDraft[`${normalized}_fields`]);
      if (current.length <= 1) return;
      current.splice(index, 1);
      this.cardLayoutDraft[`${normalized}_fields`] = dedupeFieldKeys(current);
    },
    moveCardField(entityType, index, delta) {
      const normalized = normalizeNcrmLeadString(entityType).toLowerCase();
      if (!["company", "person"].includes(normalized)) return;
      const current = dedupeFieldKeys(this.cardLayoutDraft[`${normalized}_fields`]);
      const target = index + delta;
      if (target < 0 || target >= current.length) return;
      const [item] = current.splice(index, 1);
      current.splice(target, 0, item);
      this.cardLayoutDraft[`${normalized}_fields`] = dedupeFieldKeys(current);
    },
    updateCardFieldKey(entityType, index, value) {
      const normalized = normalizeNcrmLeadString(entityType).toLowerCase();
      if (!["company", "person"].includes(normalized)) return;
      const key = normalizeNcrmLeadString(value);
      if (!key) return;
      const current = dedupeFieldKeys(this.cardLayoutDraft[`${normalized}_fields`]);
      if (index < 0 || index >= current.length) return;
      current.splice(index, 1, key);
      this.cardLayoutDraft[`${normalized}_fields`] = dedupeFieldKeys(current);
    },
    async saveCardLayoutSettings() {
      if (!this.boardId) return;
      this.cardLayoutSaving = true;
      try {
        const payload = {
          density: normalizeLeadBoardDensity(this.cardLayoutDraft?.density, LEAD_CARD_DEFAULT_LAYOUT.density),
          company_fields: dedupeFieldKeys(this.cardLayoutDraft?.company_fields),
          person_fields: dedupeFieldKeys(this.cardLayoutDraft?.person_fields),
        };
        if (!payload.company_fields.length || !payload.person_fields.length) {
          this.showSnackbar(this.$t("ncrm.leads.cardLayout.messages.fieldRequired"), "error");
          return;
        }

        if (this.cardLayoutScope === "board") {
          await axios.put(`/ncrm/leads/boards/${this.boardId}/card-layout`, payload);
        } else {
          await axios.put(`/ncrm/leads/boards/${this.boardId}/card-layout/user`, payload);
        }
        await this.loadCardLayoutConfig();
        this.cardLayoutDialog = false;
        await this.loadEntries();
        this.showSnackbar(this.$t("ncrm.leads.cardLayout.messages.saveSuccess"), "success");
      } catch (error) {
        console.error("[ncrm-leads] save card layout failed:", error);
        const apiMessage = normalizeNcrmLeadString(error?.response?.data?.error);
        this.showSnackbar(apiMessage || this.$t("ncrm.leads.cardLayout.messages.saveError"), "error");
      } finally {
        this.cardLayoutSaving = false;
      }
    },
    async resetUserCardLayout() {
      if (!this.boardId) return;
      this.cardLayoutSaving = true;
      try {
        await axios.delete(`/ncrm/leads/boards/${this.boardId}/card-layout/user`);
        await this.loadCardLayoutConfig();
        this.syncCardLayoutDraftByScope();
        await this.loadEntries();
        this.showSnackbar(this.$t("ncrm.leads.cardLayout.messages.resetSuccess"), "success");
      } catch (error) {
        console.error("[ncrm-leads] reset user card layout failed:", error);
        const apiMessage = normalizeNcrmLeadString(error?.response?.data?.error);
        this.showSnackbar(apiMessage || this.$t("ncrm.leads.cardLayout.messages.resetError"), "error");
      } finally {
        this.cardLayoutSaving = false;
      }
    },
    async saveDensityPreference(density) {
      if (!this.boardId) return;
      const normalizedDensity = normalizeLeadBoardDensity(density, this.effectiveCardLayout.density);
      if (!normalizedDensity) return;
      try {
        await axios.put(`/ncrm/leads/boards/${this.boardId}/card-layout/user`, {
          density: normalizedDensity,
          company_fields: this.effectiveCardLayout.company_fields,
          person_fields: this.effectiveCardLayout.person_fields,
        });
        this.cardLayoutConfig = {
          ...this.cardLayoutConfig,
          user_layout: {
            ...this.effectiveCardLayout,
            density: normalizedDensity,
          },
          effective_layout: {
            ...this.effectiveCardLayout,
            density: normalizedDensity,
          },
        };
      } catch (error) {
        console.error("[ncrm-leads] save density preference failed:", error);
      }
    },
    entryFieldLabel(fieldKey) {
      return buildLeadBoardEntryFieldLabel(fieldKey, this.$t);
    },
    buildStandardEntryField(entry, fieldKey) {
      const normalizedEntityType = normalizeNcrmLeadEntryEntityType(entry?.entity_type);
      const key = normalizeNcrmLeadString(fieldKey).toLowerCase();
      if (!key || key === "custom_auto") return null;

      const label = this.entryFieldLabel(key);
      if (key === "owner") {
        return { key, label, value: this.ownerLabel(entry) };
      }
      if (key === "status") {
        return {
          key,
          label,
          value: this.entryStatusLabel(entry?.status),
          variant: "chip",
          color: this.entryStatusColor(entry?.status),
        };
      }
      if (key === "entity_type") {
        const entityColor = normalizedEntityType === "person" ? "info" : "secondary";
        return {
          key,
          label,
          value: this.entityTypeLabel(entry?.entity_type),
          variant: "chip",
          color: entityColor,
        };
      }
      if (key === "next_followup") {
        return { key, label, value: this.formatDateTime(entry?.next_followup_at) };
      }
      if (key === "next_activity") {
        return {
          key,
          label,
          value: this.nextActivityInlineLabel(entry),
          overdue: this.isNextActivityOverdue(entry?.next_activity_due_at),
        };
      }
      if (key === "updated_at") {
        return { key, label, value: this.formatDateTime(entry?.updated_at) };
      }

      if (normalizedEntityType === "company") {
        const companyMap = {
          source: entry?.company_source,
          business_category: entry?.company_business_category,
          industry: entry?.company_industry,
          email_primary: entry?.company_email_primary,
          phone_primary: entry?.company_phone_primary,
          website: entry?.company_website,
        };
        if (Object.prototype.hasOwnProperty.call(companyMap, key)) {
          return { key, label, value: normalizeNcrmLeadString(companyMap[key]) || "-" };
        }
      }

      if (normalizedEntityType === "person") {
        const personMap = {
          source: entry?.person_source,
          position: entry?.person_position,
          department: entry?.person_department,
          email_primary: entry?.person_email_primary,
          phone_primary: entry?.person_phone_primary,
          linked_company: entry?.person_company_name || entry?.company_name,
        };
        if (Object.prototype.hasOwnProperty.call(personMap, key)) {
          return { key, label, value: normalizeNcrmLeadString(personMap[key]) || "-" };
        }
      }

      return null;
    },
    resolvedEntryCardFields(entry) {
      const entityType = normalizeNcrmLeadEntryEntityType(entry?.entity_type);
      if (!entityType) return [];
      const layoutFields = entityType === "company"
        ? this.effectiveCardLayout.company_fields
        : this.effectiveCardLayout.person_fields;
      const explicitCustomKeys = new Set(layoutFields.filter((key) => normalizeNcrmLeadString(key).startsWith("custom:")));
      const entryCustomFields = ensureArray(entry?.entity_custom_fields).map((item) => ({
        key: normalizeNcrmLeadString(item?.key),
        label: normalizeNcrmLeadString(item?.label) || normalizeNcrmLeadString(item?.definition_key) || "Custom field",
        value: normalizeNcrmLeadString(item?.value_text),
      }));
      const resolved = [];
      for (const key of layoutFields) {
        const normalizedKey = normalizeNcrmLeadString(key);
        if (!normalizedKey) continue;
        if (normalizedKey === "custom_auto") {
          const autoItems = entryCustomFields
            .filter((item) => item.key && item.value && !explicitCustomKeys.has(item.key))
            .slice(0, 2)
            .map((item) => ({
              key: item.key,
              label: item.label,
              value: item.value,
            }));
          resolved.push(...autoItems);
          continue;
        }
        if (normalizedKey.startsWith("custom:")) {
          const custom = entryCustomFields.find((item) => item.key === normalizedKey && item.value);
          if (custom) {
            resolved.push({
              key: normalizedKey,
              label: custom.label,
              value: custom.value,
            });
          }
          continue;
        }
        const standardField = this.buildStandardEntryField(entry, normalizedKey);
        if (standardField) {
          resolved.push(standardField);
        }
      }
      return resolved;
    },
    async initializeBoard() {
      if (!this.boardId) {
        this.showSnackbar(this.$t("ncrm.leads.board.messages.invalidBoard"), "error");
        return;
      }
      this.boardLoading = true;
      try {
        await this.fetchCurrentUser();
        const { data } = await axios.get("/ncrm/leads/boards", {
          params: { includeInactive: true },
        });
        const boards = Array.isArray(data?.items) ? data.items : [];
        this.board = boards.find((entry) => Number(entry?.id) === this.boardId) || null;
        if (!this.board) {
          this.showSnackbar(this.$t("ncrm.leads.board.messages.notFound"), "error");
          this.$router.replace({ name: "NcrmLeadsHome" }).catch(() => {});
          return;
        }
        await Promise.all([this.loadCardLayoutConfig(), this.fetchDealPipelines(), this.fetchOwnerOptions("")]);
        await this.loadEntries();
      } catch (error) {
        console.error("[ncrm-leads] init board failed:", error);
        this.showSnackbar(this.$t("ncrm.leads.board.messages.loadError"), "error");
      } finally {
        this.boardLoading = false;
      }
    },
    async loadEntries() {
      if (!this.boardId) return;
      this.entriesLoading = true;
      this.clearDragState();
      try {
        const params = {
          view: this.activeView,
          search: normalizeNcrmLeadString(this.search) || undefined,
          advancedFilters: serializeLeadBoardFilters(this.advancedFilters) || undefined,
        };
        if (this.activeView === "table") {
          params.page = this.page;
          params.limit = this.itemsPerPage;
          const sortState = this.resolveTableSortState();
          if (sortState) {
            params.sortBy = sortState.key;
            params.sortOrder = sortState.order;
          }
        }
        const { data } = await axios.get(`/ncrm/leads/boards/${this.boardId}/entries`, { params });
        this.board = data?.board || this.board;
        this.entries = Array.isArray(data?.items) ? data.items : [];
        this.stages = Array.isArray(data?.stages) ? data.stages : [];
        this.byStage = Array.isArray(data?.by_stage) ? data.by_stage : [];
        if (this.byStage.length) {
          this.syncStageVisibleCounts(this.byStage);
        }
        this.totalItems = Number(data?.total || 0);
      } catch (error) {
        console.error("[ncrm-leads] load entries failed:", error);
        this.entries = [];
        this.stages = [];
        this.byStage = [];
        this.stageVisibleCounts = {};
        this.totalItems = 0;
        this.showSnackbar(this.$t("ncrm.leads.board.messages.loadEntriesError"), "error");
      } finally {
        this.entriesLoading = false;
      }
    },
    findStageIndex(stageId) {
      const id = parsePositiveId(stageId);
      if (!id) return -1;
      return this.boardColumns.findIndex((stage) => Number(stage?.stage_id) === id);
    },
    canMoveEntry(entry, offset) {
      const index = this.findStageIndex(entry?.stage_id);
      if (index < 0) return false;
      const target = index + offset;
      return target >= 0 && target < this.boardColumns.length;
    },
    resolveMoveTargetStage(entry, offset) {
      const index = this.findStageIndex(entry?.stage_id); if (index < 0) return null;
      const targetIndex = index + offset;
      if (targetIndex < 0 || targetIndex >= this.boardColumns.length) return null;
      return this.boardColumns[targetIndex];
    },
    async moveEntryToStage(entryId, stageId) {
      const normalizedEntryId = parsePositiveId(entryId);
      const normalizedStageId = parsePositiveId(stageId);
      if (!normalizedEntryId || !normalizedStageId) return;
      const entrySnapshot = this.resolveEntrySnapshot(normalizedEntryId);
      const fallbackEntryName = this.entryDisplayName(entrySnapshot || { id: normalizedEntryId });
      const fallbackOwnerId = parsePositiveId(entrySnapshot?.owner_user_id ?? entrySnapshot?.ownerUserId), fallbackOwnerLabel = this.ownerLabel(entrySnapshot || {});
      const targetStage = (this.boardColumns || []).find((stage) => parsePositiveId(stage?.stage_id ?? stage?.id) === normalizedStageId) || null;
      const closeOpenActivities = resolveLeadTerminalTaskClosureConfirmation({ stage: targetStage, record: entrySnapshot, translate: this.$t, entityLabel: fallbackEntryName });
      if (closeOpenActivities === null) return;
      this.entryMoveLoadingId = normalizedEntryId;
      try {
        const payload = closeOpenActivities ? { stage_id: normalizedStageId, close_open_activities: true } : { stage_id: normalizedStageId };
        const { data } = await axios.patch(`/ncrm/leads/entries/${normalizedEntryId}`, payload);
        await this.loadEntries();
        const convertedDealId = parsePositiveId(data?.conversion?.deal_id);
        if (convertedDealId) {
          this.showSnackbar(this.$t("ncrm.leads.board.messages.autoConverted", { id: convertedDealId }), "success");
        }
        await this.maybeOpenOwnerClaimDialog({
          entryId: normalizedEntryId,
          entryName: fallbackEntryName,
          ownerUserId: parsePositiveId(data?.entry?.owner_user_id) || fallbackOwnerId,
          ownerLabel: normalizeNcrmLeadString(data?.entry?.owner_name) || fallbackOwnerLabel,
        });
      } catch (error) {
        console.error("[ncrm-leads] move entry failed:", error);
        const responseCode = normalizeNcrmLeadString(error?.response?.data?.code).toUpperCase();
        if (responseCode === "AUTO_CONVERT_REQUIRES_INPUT") {
          this.openMoveConvertDialog({
            entryId: normalizedEntryId,
            fallbackStageId: normalizedStageId,
            defaults: error?.response?.data?.conversion_defaults || {},
          });
          this.showSnackbar(this.$t("ncrm.leads.board.messages.autoConvertNeedsInput"), "warning");
        } else {
          const apiMessage = String(error?.response?.data?.error || "").trim();
          this.showSnackbar(apiMessage || this.$t("ncrm.leads.board.messages.moveError"), "error");
        }
      } finally {
        this.entryMoveLoadingId = null;
      }
    },
    async moveEntryByOffset(entry, offset) {
      const entryId = parsePositiveId(entry?.id);
      const targetStage = this.resolveMoveTargetStage(entry, offset);
      if (!entryId || !targetStage?.stage_id) return;
      await this.moveEntryToStage(entryId, targetStage.stage_id);
    },
    ensureMoveConvertSelection() {
      const selectedPipelineId = parsePositiveId(
        this.moveConvertDialog.dealPipelineId || this.selectedMoveConvertPipelineId
      );
      if (selectedPipelineId !== parsePositiveId(this.moveConvertDialog.dealPipelineId)) {
        this.moveConvertDialog.dealPipelineId = selectedPipelineId;
      }
      const stageOptions = this.quickDealStageOptions;
      const currentStageId = parsePositiveId(this.moveConvertDialog.dealStageId);
      if (!currentStageId || !stageOptions.some((entry) => entry.id === currentStageId)) {
        this.moveConvertDialog.dealStageId = stageOptions[0]?.id || null;
      }
      const leadStageOptions = this.quickConvertStageOptions;
      const currentLeadStageId = parsePositiveId(this.moveConvertDialog.leadStageId);
      if (!currentLeadStageId || !leadStageOptions.some((entry) => entry.id === currentLeadStageId)) {
        this.moveConvertDialog.leadStageId = leadStageOptions[0]?.id || this.defaultStageId || null;
      }
    },
    onMoveConvertPipelineChanged(value) {
      this.moveConvertDialog.dealPipelineId = parsePositiveId(value) || null;
      this.ensureMoveConvertSelection();
    },
    async openMoveConvertDialog({ entryId, defaults = {}, fallbackStageId = null } = {}) {
      const normalizedEntryId = parsePositiveId(entryId);
      if (!normalizedEntryId) return;
      await Promise.all([this.fetchOwnerOptions(""), this.fetchDealPipelines()]);
      this.moveConvertDialog.entryId = normalizedEntryId;
      this.moveConvertDialog.dealName = "";
      this.moveConvertDialog.note = "";
      this.moveConvertDialog.ownerUserId = parsePositiveId(defaults?.owner_user_id) || null;
      this.moveConvertDialog.leadStageId =
        parsePositiveId(defaults?.lead_stage_id) || parsePositiveId(fallbackStageId) || this.defaultStageId || null;
      this.moveConvertDialog.dealPipelineId = parsePositiveId(defaults?.deal_pipeline_id) || this.selectedMoveConvertPipelineId;
      this.moveConvertDialog.dealStageId = parsePositiveId(defaults?.deal_stage_id) || null;
      this.ensureMoveConvertSelection();
      this.moveConvertDialog.open = true;
    },
    async submitMoveConvert() {
      const entryId = parsePositiveId(this.moveConvertDialog.entryId);
      if (!entryId) return;
      const entrySnapshot = this.resolveEntrySnapshot(entryId);
      const selectedOwnerUserId = parsePositiveId(this.moveConvertDialog.ownerUserId);
      const fallbackOwnerId =
        selectedOwnerUserId || parsePositiveId(entrySnapshot?.owner_user_id ?? entrySnapshot?.ownerUserId);
      const fallbackOwnerLabel = selectedOwnerUserId
        ? this.ownerOptionLabel(selectedOwnerUserId)
        : this.ownerLabel(entrySnapshot || {});
      const fallbackEntryName = this.entryDisplayName(entrySnapshot || { id: entryId });
      const dealPipelineId = parsePositiveId(this.moveConvertDialog.dealPipelineId || this.selectedMoveConvertPipelineId);
      const dealStageId = parsePositiveId(this.moveConvertDialog.dealStageId);
      if (!dealPipelineId || !dealStageId) {
        this.showSnackbar(this.$t("ncrm.leads.details.messages.convertDealTargetMissing"), "warning");
        return;
      }
      this.moveConvertDialog.loading = true;
      try {
        const payload = {
          deal_name: normalizeNcrmLeadString(this.moveConvertDialog.dealName) || undefined,
          deal_pipeline_id: dealPipelineId,
          deal_stage_id: dealStageId,
          note: normalizeNcrmLeadString(this.moveConvertDialog.note) || undefined,
        };
        const leadStageId = parsePositiveId(this.moveConvertDialog.leadStageId);
        const ownerUserId = parsePositiveId(this.moveConvertDialog.ownerUserId);
        if (leadStageId) payload.stage_id = leadStageId;
        if (ownerUserId) payload.owner_user_id = ownerUserId;

        const { data } = await axios.post(`/ncrm/leads/entries/${entryId}/convert`, payload);
        const convertedDealId = parsePositiveId(data?.conversion?.deal_id || data?.entry?.converted_deal_id);
        this.moveConvertDialog.open = false;
        await this.loadEntries();
        this.showSnackbar(
          convertedDealId
            ? this.$t("ncrm.leads.board.messages.autoConverted", { id: convertedDealId })
            : this.$t("ncrm.leads.details.messages.converted"),
          "success"
        );
        await this.maybeOpenOwnerClaimDialog({
          entryId,
          entryName: fallbackEntryName,
          ownerUserId: parsePositiveId(data?.entry?.owner_user_id) || fallbackOwnerId,
          ownerLabel: normalizeNcrmLeadString(data?.entry?.owner_name) || fallbackOwnerLabel,
        });
      } catch (error) {
        console.error("[ncrm-leads] manual fallback convert failed:", error);
        const apiMessage = normalizeNcrmLeadString(error?.response?.data?.error);
        this.showSnackbar(apiMessage || this.$t("ncrm.leads.details.messages.convertError"), "error");
      } finally {
        this.moveConvertDialog.loading = false;
      }
    },
    clearRowConditionTimers(row) {
      (row?.conditions || []).forEach((condition) => {
        if (condition?.search_debounce_id) {
          clearTimeout(condition.search_debounce_id);
          condition.search_debounce_id = null;
        }
      });
    },
    clearConditionTimers() {
      (this.addRows || []).forEach((row) => this.clearRowConditionTimers(row));
    },
    createConditionRow() {
      this.conditionSeq += 1;
      return {
        _uid: `condition-${this.conditionSeq}-${Date.now()}`,
        field_key: "",
        operator: "equals",
        value: "",
        value_suggestions: [],
        value_loading: false,
        value_request_seq: 0,
        search_debounce_id: null,
      };
    },
    createBulkRow() {
      const defaultEntityType = this.boardEntityScope === "company_only"
        ? "company"
        : this.boardEntityScope === "person_only"
          ? "person"
          : "company";
      return {
        _uid: toUid(),
        owner_user_id: this.currentUserId,
        entity_type: defaultEntityType,
        stage_id: this.defaultStageId,
        next_followup_at: "",
        conditions_logic: "or",
        conditions: [this.createConditionRow()],
      };
    },
    filterFieldOptionsForEntity(entityType) {
      const normalized = normalizeNcrmLeadString(entityType).toLowerCase();
      if (normalized !== "company" && normalized !== "person") return [];
      return Array.isArray(this.filterFieldsByEntity?.[normalized]) ? this.filterFieldsByEntity[normalized] : [];
    },
    openAddDialog() {
      this.clearConditionTimers();
      this.addRows = [this.createBulkRow()];
      this.addValidation = null;
      this.addDialog = true;
      this.fetchOwnerOptions("");
      if (this.boardEntityScope === "both") {
        this.fetchFilterFields("company");
        this.fetchFilterFields("person");
      } else if (this.boardEntityScope === "company_only") {
        this.fetchFilterFields("company");
      } else if (this.boardEntityScope === "person_only") {
        this.fetchFilterFields("person");
      }
    },
    addBulkRow() {
      this.addRows.push(this.createBulkRow());
      this.markBulkRowsDirty();
    },
    removeBulkRow(index) {
      if (this.addRows.length <= 1) return;
      const row = this.addRows[index];
      this.clearRowConditionTimers(row);
      this.addRows.splice(index, 1);
      this.markBulkRowsDirty();
    },
    onBulkRowEntityTypeChanged(row) {
      const normalized = normalizeNcrmLeadString(row?.entity_type).toLowerCase();
      if (normalized !== "company" && normalized !== "person") {
        row.conditions = [this.createConditionRow()];
        row.conditions_logic = "or";
        this.markBulkRowsDirty();
        return;
      }
      this.clearRowConditionTimers(row);
      row.conditions_logic = "or";
      row.conditions = [this.createConditionRow()];
      this.fetchFilterFields(normalized);
      this.markBulkRowsDirty();
    },
    addConditionRow(row) {
      if (!row || !Array.isArray(row.conditions)) return;
      row.conditions.push(this.createConditionRow());
      this.markBulkRowsDirty();
    },
    removeConditionRow(row, index) {
      if (!row || !Array.isArray(row.conditions) || row.conditions.length <= 1) return;
      const condition = row.conditions[index];
      if (condition?.search_debounce_id) {
        clearTimeout(condition.search_debounce_id);
      }
      row.conditions.splice(index, 1);
      this.markBulkRowsDirty();
    },
    onConditionFieldChanged(row, condition) {
      if (!condition) return;
      condition.value = "";
      condition.value_suggestions = [];
      condition.value_request_seq = 0;
      this.markBulkRowsDirty();
      if (!condition.field_key) return;
      this.fetchConditionValueSuggestions(row, condition, "");
    },
    onConditionOperatorChanged(row, condition) {
      if (!condition) return;
      if (condition.operator === "is_empty" || condition.operator === "is_not_empty") {
        condition.value = "";
      }
      condition.value_suggestions = [];
      condition.value_request_seq = 0;
      this.markBulkRowsDirty();
      if (condition.operator !== "is_empty" && condition.operator !== "is_not_empty" && condition.field_key) {
        this.fetchConditionValueSuggestions(row, condition, "");
      }
    },
    ensureConditionSuggestionsLoaded(row, condition) {
      if (!condition?.field_key) return;
      if (condition.operator === "is_empty" || condition.operator === "is_not_empty") return;
      if (Array.isArray(condition.value_suggestions) && condition.value_suggestions.length > 0) return;
      this.fetchConditionValueSuggestions(row, condition, "");
    },
    scheduleConditionValueSearch(row, condition, search) {
      if (!condition?.field_key) return;
      if (condition.operator === "is_empty" || condition.operator === "is_not_empty") return;
      if (condition.search_debounce_id) {
        clearTimeout(condition.search_debounce_id);
      }
      condition.search_debounce_id = setTimeout(() => {
        this.fetchConditionValueSuggestions(row, condition, search);
      }, 220);
    },
    async fetchConditionValueSuggestions(row, condition, search = "") {
      if (!this.boardId || !row || !condition) return;
      const entityType = normalizeNcrmLeadString(row.entity_type).toLowerCase();
      if (entityType !== "company" && entityType !== "person") return;
      const fieldKey = normalizeNcrmLeadString(condition.field_key);
      if (!fieldKey) return;
      const operator = normalizeNcrmLeadString(condition.operator).toLowerCase() || "equals";
      if (operator === "is_empty" || operator === "is_not_empty") return;

      const requestSeq = Number(condition.value_request_seq || 0) + 1;
      condition.value_request_seq = requestSeq;
      condition.value_loading = true;
      try {
        const { data } = await axios.get(`/ncrm/leads/boards/${this.boardId}/filter-values`, {
          params: {
            entityType,
            fieldKey,
            operator,
            search: normalizeNcrmLeadString(search) || undefined,
            limit: 20,
          },
        });
        if (requestSeq !== Number(condition.value_request_seq)) return;
        condition.value_suggestions = (Array.isArray(data?.items) ? data.items : [])
          .map((item) => normalizeNcrmLeadString(item))
          .filter(Boolean);
      } catch (error) {
        console.error("[ncrm-leads] load filter values failed:", error);
        if (requestSeq !== Number(condition.value_request_seq)) return;
        condition.value_suggestions = [];
      } finally {
        if (requestSeq === Number(condition.value_request_seq)) {
          condition.value_loading = false;
        }
      }
    },
    async fetchFilterFields(entityType) {
      const normalizedEntityType = normalizeNcrmLeadString(entityType).toLowerCase();
      if (normalizedEntityType !== "company" && normalizedEntityType !== "person") return;
      if (!this.boardId) return;
      const requestSeq = Number(this.filterFieldsRequestSeqByEntity?.[normalizedEntityType] || 0) + 1;
      this.filterFieldsRequestSeqByEntity = {
        ...this.filterFieldsRequestSeqByEntity,
        [normalizedEntityType]: requestSeq,
      };
      this.filterFieldsLoadingByEntity = {
        ...this.filterFieldsLoadingByEntity,
        [normalizedEntityType]: true,
      };
      try {
        const { data } = await axios.get(`/ncrm/leads/boards/${this.boardId}/filter-fields`, {
          params: {
            entityType: normalizedEntityType,
          },
        });
        if (requestSeq !== Number(this.filterFieldsRequestSeqByEntity?.[normalizedEntityType])) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        this.filterFieldsByEntity = {
          ...this.filterFieldsByEntity,
          [normalizedEntityType]: dedupeLeadFilterFieldItems(
            items.map((entry) => ({
              key: normalizeNcrmLeadString(entry?.key),
              label: normalizeNcrmLeadString(entry?.label) || normalizeNcrmLeadString(entry?.key),
              source: normalizeNcrmLeadString(entry?.source) || "standard",
            }))
          ),
        };
      } catch (error) {
        console.error("[ncrm-leads] load filter fields failed:", error);
      } finally {
        if (requestSeq === Number(this.filterFieldsRequestSeqByEntity?.[normalizedEntityType])) {
          this.filterFieldsLoadingByEntity = {
            ...this.filterFieldsLoadingByEntity,
            [normalizedEntityType]: false,
          };
        }
      }
    },
    markBulkRowsDirty() {
      this.addValidation = null;
    },
    serializeBulkRows() {
      return (this.addRows || []).map((row) => {
        const payload = {
          owner_user_id: parsePositiveId(row.owner_user_id),
          entity_type: normalizeNcrmLeadString(row.entity_type).toLowerCase(),
          stage_id: parsePositiveId(row.stage_id) || this.defaultStageId,
          conditions_logic: normalizeNcrmLeadString(row.conditions_logic).toLowerCase() || "or",
          conditions: (Array.isArray(row.conditions) ? row.conditions : [])
            .map((condition) => ({
              field_key: normalizeNcrmLeadString(condition?.field_key),
              operator: normalizeNcrmLeadString(condition?.operator).toLowerCase() || "equals",
              value: normalizeNcrmLeadString(condition?.value),
            }))
            .filter((condition) => condition.field_key),
        };

        const nextFollowup = normalizeNcrmLeadString(row.next_followup_at);
        if (nextFollowup) {
          payload.next_followup_at = nextFollowup;
        }
        return payload;
      });
    },
    async validateBulkAdd() {
      if (!this.boardId) return;
      this.addValidating = true;
      try {
        const payload = { entries: this.serializeBulkRows() };
        const { data } = await axios.post(`/ncrm/leads/boards/${this.boardId}/entries/validate-bulk`, payload);
        this.addValidation = data || null;
        if (data?.valid) {
          this.showSnackbar(this.$t("ncrm.leads.addDialog.messages.validated"), "success");
        }
      } catch (error) {
        console.error("[ncrm-leads] validate bulk failed:", error);
        const details = error?.response?.data;
        this.addValidation = {
          valid: false,
          errors: Array.isArray(details?.errors) ? details.errors : [],
          warnings: Array.isArray(details?.warnings) ? details.warnings : [],
          preview_items: Array.isArray(details?.preview_items) ? details.preview_items : [],
        };
        const apiMessage = String(details?.error || "").trim();
        this.showSnackbar(apiMessage || this.$t("ncrm.leads.addDialog.messages.validateError"), "error");
      } finally {
        this.addValidating = false;
      }
    },
    async submitBulkAdd() {
      if (!this.boardId) return;
      if (!this.addValidation || !this.addValidation.valid) {
        await this.validateBulkAdd();
        if (!this.addValidation || !this.addValidation.valid) return;
      }

      this.addSaving = true;
      try {
        const payload = { entries: this.serializeBulkRows() };
        const { data } = await axios.post(`/ncrm/leads/boards/${this.boardId}/entries/bulk-add`, payload);
        const createdCount = Number(data?.created_count || 0);
        this.addDialog = false;
        this.showSnackbar(this.$t("ncrm.leads.addDialog.messages.saveSuccess", { count: createdCount }), "success");
        await this.loadEntries();
      } catch (error) {
        console.error("[ncrm-leads] bulk add failed:", error);
        const details = error?.response?.data;
        this.addValidation = {
          valid: false,
          errors: Array.isArray(details?.errors) ? details.errors : [],
          warnings: Array.isArray(details?.warnings) ? details.warnings : [],
          preview_items: Array.isArray(details?.preview_items) ? details.preview_items : [],
        };
        const apiMessage = String(details?.error || "").trim();
        this.showSnackbar(apiMessage || this.$t("ncrm.leads.addDialog.messages.saveError"), "error");
      } finally {
        this.addSaving = false;
      }
    },
    formatValidationError(error = {}) {
      const rowIndex = Number(error?.row_index || 0);
      const field = normalizeNcrmLeadString(error?.field);
      const code = normalizeNcrmLeadString(error?.code);
      const message = normalizeNcrmLeadString(error?.message);
      const rowLabel = rowIndex > 0
        ? this.$t("ncrm.leads.addDialog.validationRowLabel", { index: rowIndex })
        : this.$t("ncrm.leads.addDialog.validationRowLabelUnknown");
      const parts = [rowLabel];
      if (field) parts.push(field);
      if (code) parts.push(code);
      if (message) parts.push(message);
      return parts.join(" - ");
    },
    previewRowIsExisting(item = {}) {
      return Boolean(item?.already_in_board);
    },
    previewRowClass(item = {}) {
      return this.previewRowIsExisting(item) ? "ncrm-lead-board__preview-row--existing" : "";
    },
    previewRowProps(payload = {}) {
      const item = payload?.item?.raw ?? payload?.item ?? {};
      return this.previewRowIsExisting(item) ? { class: "ncrm-lead-board__preview-row--existing" } : {};
    },
    previewStateLabel(item = {}) {
      return this.previewRowIsExisting(item)
        ? this.$t("ncrm.leads.addDialog.previewStates.alreadyInBoard")
        : this.$t("ncrm.leads.addDialog.previewStates.newEntry");
    },
    previewStateClass(item = {}) {
      return this.previewRowIsExisting(item)
        ? "ncrm-lead-board__state-chip--existing"
        : "ncrm-lead-board__state-chip--new";
    },
    previewStateIcon(item = {}) {
      return this.previewRowIsExisting(item) ? "mdi-check-circle-outline" : "mdi-plus-circle-outline";
    },
    async fetchCurrentUser() {
      const existingUserId = parsePositiveId(this.currentUserId);
      if (existingUserId) return existingUserId;
      const resolvedId = await resolveCurrentUserId(axios);
      this.currentUserId = resolvedId || null;
      return this.currentUserId;
    },
    async fetchDealPipelines() {
      const requestSeq = Number(this.dealPipelineRequestSeq || 0) + 1;
      this.dealPipelineRequestSeq = requestSeq;
      try {
        const { data } = await axios.get("/ncrm/deals/pipelines", {
          params: {
            include_stages: true,
            include_inactive: false,
            ensure_default: true,
          },
        });
        if (requestSeq !== Number(this.dealPipelineRequestSeq)) return;
        this.dealPipelines = Array.isArray(data?.items) ? data.items : [];
      } catch (error) {
        console.error("[ncrm-leads] load deal pipelines failed:", error);
        if (requestSeq !== Number(this.dealPipelineRequestSeq)) return;
        this.dealPipelines = [];
      }
    },
    handleOwnerSearch(search) {
      if (this.ownerSearchDebounceId) clearTimeout(this.ownerSearchDebounceId);
      this.ownerSearchDebounceId = setTimeout(() => this.fetchOwnerOptions(search), 220);
    },
    async fetchOwnerOptions(search = "") {
      const requestSeq = ++this.ownerRequestSeq;
      this.ownerOptionsLoading = true;
      try {
        const normalizedSearch = normalizeNcrmLeadString(search);
        const { data } = await axios.get("/ncrm/leads/owners", {
          params: {
            search: normalizedSearch || undefined,
            limit: 100,
          },
        });
        if (requestSeq !== this.ownerRequestSeq) return;
        this.ownerOptions = (Array.isArray(data?.items) ? data.items : []).map((entry) => ({
          id: Number(entry.id),
          label: String(entry.display_name || entry.name || entry.email || `#${entry.id}`).trim(),
        }));
      } catch (error) {
        console.error("[ncrm-leads] load owners failed:", error);
      } finally {
        this.ownerOptionsLoading = false;
      }
    },
  },
};
</script>

<style scoped src="./NcrmLeadBoard.styles.css"></style>
